// /bootcampy/:id — detail běhu: nastavení, lekce, účastnice a docházka (22.8.2026).
//
// Rezervace i docházka jdou přes /api/bootcampy (akce rezervovat, zrusit-rezervaci,
// dochazka). Vstupy se nikde neukládají, počítají se z rezervací, takže tabulka
// nemůže zastarat.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Flame, ArrowLeft, CalendarDays, Plus, Save, X, Trash2, Check, Clock, Phone, Mail, Pencil, Copy,
  CheckCircle2, XCircle, Circle, Ban, Wand2, ChevronDown, ChevronRight, Maximize2, RefreshCw, MapPin, Users,
  CreditCard, SlidersHorizontal, CalendarOff, Archive, ArchiveRestore, PauseCircle, Play,
} from "lucide-react";
import { UDALOST as UDALOST_ZALOZKA } from "../../lib/taby.js";
import Card from "../../components/Card.jsx";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote, Hlaska, SkeletonRows, EmptyState, Dlazdice, Zapinatko } from "../../components/ui.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { formatDate as fmtDate } from "../../lib/format.js";
import PoleDatumDo from "../../components/PoleDatumDo.jsx";
import { RozvrhEditor, MistaEditor, PauzyEditor, Blok, DNY } from "../../components/BootcampNastaveni.jsx";

// Stejná grafika jako u stavu klienta v sekci Klienti.
const STAV_BEHU = {
  pripravovany: { label: "Připravovaný", cls: "bg-slate-100 text-slate-600 border-slate-200", Ikona: CalendarDays },
  probiha:      { label: "Probíhá",      cls: "bg-emerald-100 text-emerald-700 border-emerald-200", Ikona: CheckCircle2 },
  ukonceny:     { label: "Ukončený",     cls: "bg-slate-100 text-slate-500 border-slate-200", Ikona: Ban },
  // 3. 11. 2026: lokalita, která se zatím nespouští (Letná). Není ukončená,
  // jen čeká. Termíny se jí nezakládají, zprávy nechodí, na webu je skrytá.
  nespusteno:   { label: "Zatím nespuštěno", cls: "bg-amber-50 text-amber-800 border-amber-200", Ikona: PauseCircle },
};

// V seznamu na telefonu nese stav ikona v barevném kolečku, ne slovní pilulka.
// Pilulka Zaplaceno brala skoro sto bodů a delší jména se pak lámala do dvou
// řádků. Celé znění je pod seznamem ve vysvětlivce a v detailu ženy (14.9.2026).
const STAVY_UCASTI = [
  { id: "prihlasena", label: "Čeká na platbu", tone: "bg-amber-100 text-amber-700", Ikona: Clock },
  { id: "zaplaceno", label: "Zaplaceno", tone: "bg-emerald-100 text-emerald-700", Ikona: Check },
  { id: "zrusena", label: "Zrušena", tone: "bg-slate-100 text-slate-500", Ikona: X },
];

function den(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : fmtDate(d);
}
function denVTydnu(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  if (isNaN(d.getTime())) return "";
  return ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"][d.getUTCDay()];
}
function Delic() {
  return <span aria-hidden className="w-px h-3 bg-current opacity-40" />;
}
// Stejný výpočet jako na serveru (bootcampLib.stavBehu), aby náhled
// v nastavení odpovídal tomu, co se po uložení opravdu uloží.
function stavZDat(b) {
  if (!b) return "pripravovany";
  if (b.ukoncen_rucne) return "ukonceny";
  if (b.nespusteno) return "nespusteno";
  const dnes = new Date().toISOString().substring(0, 10);
  const od = String(b.datum_od || "").substring(0, 10);
  const doD = String(b.datum_do || "").substring(0, 10);
  if (doD && dnes > doD) return "ukonceny";
  if (od && dnes >= od) return "probiha";
  return "pripravovany";
}
function Pole({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

// Detail běhu. Otevírá se dvěma způsoby, stejně jako úkoly:
//   fullPage=false → překryv přes stránku, jako detail klienta
//   fullPage=true  → běžný obsah pod horní lištou a řadou záložek (adresa ?beh=ID)
export default function BootcampDetail({ id, onClose, fullPage = false, onChanged }) {
  const navigate = useNavigate();
  const { data, loading, refetch, refreshing } = useApiData(`/api/bootcampy?id=${id}`);
  // 16.9.2026: bez filtru na trenery. Bootcamp muze vest kdokoli z tymu, treba
  // majitel nez se najde BC trener, a zakladat mu kvuli tomu profil trenera
  // nedava smysl. Seznam je kratky, takze vsichni aktivni staci.
  const { data: hrData } = useApiData("/api/hr-list-people?status=aktivni");
  // Seznam ostatnich lokalit, kam jde presunout klientky (slouceni dvou behu).
  const { data: seznamLokalit } = useApiData("/api/bootcampy");
  const [chyba, setChyba] = useState("");
  const [presun, setPresun] = useState(null);   // { do_bootcamp_id, platnost_do } nebo null
  const [hlaska, setHlaska] = useState("");
  const [busy, setBusy] = useState(false);
  // Lekcí bývá šestnáct a víc, sbalené je to přehlednější. Otevřený zůstává
  // jen seznam účastnic, ten je při otevření detailu nejužitečnější.
  const [otevreno, setOtevreno] = useState({ nastaveni: false, lekce: false, ucastnice: false, dochazka: false });
  const [confirm, setConfirm] = useState(null);      // { title, description, confirmLabel, variant, run }
  const [bunka, setBunka] = useState(null);          // { lekce, ucastnice, rezervace }
  const [novaUcastnice, setNovaUcastnice] = useState(null);
  // 7.9.2026: náhradní lekce za zrušenou — trenér ji smí založit taky.
  const [novaLekce, setNovaLekce] = useState(null);
  const [editUcastnice, setEditUcastnice] = useState(null);
  const [nastaveni, setNastaveni] = useState(null);
  const [rozbalenaLekce, setRozbalenaLekce] = useState(null);   // id lekce s otevřenou docházkou
  const [pridavana, setPridavana] = useState("");               // id účastnice ve výběru „kdo přišel navíc“
  // 14.9.2026: na telefonu se stránka skládá jinak. Trenér ji otevírá skoro
  // vždycky kvůli docházce dnešní lekce, takže ta je nahoře a zbytek je
  // v záložkách. Na počítači zůstává původní rozvržení se čtyřmi sekcemi.
  const uzka = useUzkaObrazovka();
  const [zalozka, setZalozka] = useState("lekce");

  const refs = { nastaveni: useRef(null), lekce: useRef(null), ucastnice: useRef(null), dochazka: useRef(null) };
  const [otevrenaDnes, setOtevrenaDnes] = useState(false);
  const [otevrenaZena, setOtevrenaZena] = useState(null);       // id ženy s rozbaleným detailem

  const role = getCurrentUser()?.role || "";
  const jeManager = ["owner", "manager", "head_coach", "csm"].includes(role);
  // 24.8.2026 podle zadani ownera: nastaveni behu, generovani lekci a mazani
  // patri ownerovi a CSM. Manazer bootcamp vidi, ale resi na nem dochazku
  // a rezervace, do nastaveni mu nic neni. Trener totez u sveho behu.
  const smiNastaveni = ["owner", "csm"].includes(role);
  // 16.9.2026 (zadani Ladi): mazani lokality je vyhradne ownerovo. Drive tady
  // stalo jeOwner = smiNastaveni, takze tlacitko Smazat videl i CSM.
  const jeOwner = role === "owner";

  const beh = data?.bootcamp || null;
  const lekce = useMemo(() => data?.lekce || [], [data]);
  const ucastnice = useMemo(() => data?.ucastnice || [], [data]);
  const rezervace = useMemo(() => data?.rezervace || [], [data]);
  // Server řekne, jestli přihlášený člověk smí sahat na lekce a docházku.
  // Manažerské role vždycky, trenér jen u svého běhu.
  const muzeLekce = !!data?.muzeLekce;

  // Stejné mapování jako ClientDetiSection: id z HR = trainers.id.
  const treneri = useMemo(() => {
    const lide = (hrData && hrData.people) || [];
    return lide
      .filter((p) => p.active !== false)
      .map((p) => ({ id: p.id, jmeno: p.display_name }))
      .filter((t) => t.id && t.jmeno)
      .sort((a, b) => a.jmeno.localeCompare(b.jmeno, "cs"));
  }, [hrData]);

  // Rychlé hledání rezervace pro dvojici lekce + účastnice.
  const rezMapa = useMemo(() => {
    const m = new Map();
    for (const r of rezervace) if (r.stav !== "zrusena") m.set(`${r.lekce_id}:${r.ucastnice_id}`, r);
    return m;
  }, [rezervace]);

  // Po rozbalení sekce k ní plynule doskrolovat, ať je vidět, co se otevřelo.
  function prepni(klic) {
    setOtevreno((o) => {
      const nove = { ...o, [klic]: !o[klic] };
      if (nove[klic]) {
        setTimeout(() => refs[klic].current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
      return nove;
    });
  }

  // bez_konce je jen ovladac formulare, v databazi je to prazdne datum_do.
  useEffect(() => {
    if (beh && !nastaveni) setNastaveni({ ...beh, tarify: beh.tarify || [], bez_konce: !beh.datum_do });
  }, [beh]); // eslint-disable-line

  async function posli(telo, hotovoText) {
    setChyba(""); setHlaska(""); setBusy(true);
    try {
      const r = await apiFetch("/api/bootcampy", { method: "POST", body: JSON.stringify(telo) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Nepodařilo se to");
      if (hotovoText) setHlaska(typeof hotovoText === "function" ? hotovoText(j) : hotovoText);
      refetch(true);
      onChanged?.();
      return j;
    } catch (e) {
      setChyba(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  }

  // Přepnutí do záložky adminu. Ctrl nebo Cmd drží zvyk prohlížeče a otevře okno.
  function doZalozky(e) {
    const kam = `/bootcampy?beh=${id}`;
    if (e.ctrlKey || e.metaKey) { window.open(kam, "_blank"); return; }
    window.dispatchEvent(new CustomEvent(UDALOST_ZALOZKA, { detail: { to: kam, title: beh?.nazev || "Bootcamp" } }));
    onClose?.();
  }

  if (loading && !beh) return <Obal fullPage={fullPage} onClose={onClose} onZalozka={doZalozky}><SkeletonRows rows={5} /></Obal>;
  if (!beh) return (
    <Obal fullPage={fullPage} onClose={onClose} onZalozka={doZalozky}>
      <EmptyState
        icon={Flame}
        title="Běh nenalezen"
        action={<Link to="/bootcampy" className="btn btn-secondary">Zpět na přehled</Link>}
      />
    </Obal>
  );

  const zaplacenych = ucastnice.filter((u) => u.stav === "zaplaceno").length;

  // Dnešní lekce, a když dnes žádná není, nejbližší budoucí. Podle ní se
  // na telefonu kreslí karta nahoře.
  const dnesIso = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague" }).format(new Date());
  const lekceDnes = (() => {
    const platne = lekce.filter((l) => !l.zruseno);
    const dnes = platne.find((l) => String(l.datum).substring(0, 10) === dnesIso);
    if (dnes) return { lekce: dnes, jeDnes: true };
    const pristi = platne
      .filter((l) => String(l.datum).substring(0, 10) > dnesIso)
      .sort((x, y) => String(x.datum).localeCompare(String(y.datum)))[0];
    return pristi ? { lekce: pristi, jeDnes: false } : null;
  })();

  // Kolik žen je na termínu přihlášeno a jestli u nich už někdo docházku zapsal.
  function stavDochazky(l) {
    const na = rezervace.filter((r) => r.lekce_id === l.id && r.stav !== "zrusena");
    const zapsanych = na.filter((r) => r.stav !== "rezervovano" && r.stav !== "prihlasena").length;
    return { prihlasenych: na.length, zapsanych, hotovo: na.length > 0 && zapsanych === na.length };
  }

  // Docházka jedné lekce i se všemi akcemi. Používá ji tabulka na počítači,
  // seznam lekcí na telefonu i karta dnešní lekce, ať je to na jednom místě.
  function dochazkaLekce(l) {
    return (
      <DochazkaLekce
        muzeZapisovat={muzeLekce}
        lekce={l}
        ucastnice={ucastnice}
        rezervace={rezervace}
        busy={busy}
        pridavana={pridavana}
        setPridavana={setPridavana}
        onDochazka={(rid, stav) => posli({ akce: "dochazka", id: rid, stav }, "Zapsáno.")}
        onOdebrat={(rid, jmeno) => setConfirm({
          title: "Odebrat z termínu?",
          description: `${jmeno || "Účastnice"} se z termínu ${den(l.datum)} odebere a vstup se jí vrátí zpátky. Přihlásit se může znovu sama nebo ji přidáte tady.`,
          confirmLabel: "Odebrat", variant: "warning",
          run: () => posli({ akce: "zrusit-rezervaci", id: rid, propadne: false }, "Odebráno, vstup se vrátil."),
        })}
        onPridat={(uid) => {
          // Budoucí termín se jen rezervuje. Označit, že dorazila,
          // jde až po tréninku, jinak by docházka lhala dopředu.
          const budouci = zacatekLekceKlient(l) > Date.now();
          return posli(
            { akce: "rezervovat", lekce_id: l.id, ucastnice_id: uid, stav: budouci ? "rezervovano" : "dorazila" },
            budouci ? "Přihlášena na termín." : "Přidána a označena, že dorazila.");
        }}
        onPoznamka={(text) => posli({ akce: "poznamka-lekce", id: l.id, poznamka: text }, "Poznámka uložena.")}
      />
    );
  }

  // Smazání termínu, na který nikdo není přihlášený (16.9.2026). Zrušení
  // posílá SMS, což u termínu, který vznikl omylem, nedává smysl. Proto se
  // nabízí jen u prázdné lekce, jinak zůstává jediná cesta Zrušit lekci.
  // Trenér u konkrétní lekce (16.9.2026, zadání Ládi). Na Letné se trenéři
  // střídají a zaskakují, takže to musí jít přepnout přímo v řádku.
  //
  // Posílají se i ostatní pole lekce. Server je nepřepisuje COALESCE, takže
  // kdyby se poslal jen trenér, smazal by se čas do, kapacita, místo i poznámka.
  function zmenTrenera(l, trenerId) {
    const kdo = (treneri.find((t) => String(t.id) === String(trenerId)) || {}).jmeno;
    posli({
      akce: "ulozit-lekci", id: l.id, bootcamp_id: beh.id,
      fields: {
        cas_do: l.cas_do || "",
        kapacita: l.kapacita == null ? "" : l.kapacita,
        trener_id: trenerId || "",
        misto: l.misto || "",
        poznamka: l.poznamka || "",
      },
    }, kdo ? `Lekci ${den(l.datum)} vede ${kdo}.` : `Lekce ${den(l.datum)} je bez trenéra.`);
  }

  function smazatLekci(l) {
    setConfirm({
      title: "Smazat termín?",
      description: `${denVTydnu(l.datum)} ${den(l.datum)} v ${l.cas_od}. Nikdo na něj není přihlášený, takže se jen smaže a nikomu nic nechodí. Tohle nejde vrátit.`,
      confirmLabel: "Smazat termín", variant: "danger",
      run: () => posli({ akce: "smazat-lekci", id: l.id }, "Termín smazán."),
    });
  }

  // Zrušení nebo obnovení lekce. Stejné z tabulky i ze seznamu na telefonu.
  function zrusitLekci(l) {
    setConfirm(l.zruseno ? {
      title: "Obnovit lekci?",
      description: "Lekce bude zase platit. Rezervace se ale samy nevrátí, klientky se musí přihlásit znovu.",
      confirmLabel: "Obnovit", variant: "primary",
      run: () => posli({ akce: "zrusit-lekci", id: l.id, zruseno: false }, "Lekce obnovena."),
    } : {
      title: "Zrušit lekci?",
      description: "Všem přihlášeným se zruší rezervace, vstup se jim vrátí a rovnou jim odejde SMS, že termín odpadá. DO DVOU DNŮ jim musíš dát vědět náhradní termín, SMS jim to slibuje.",
      confirmLabel: "Zrušit lekci", variant: "warning",
      run: () => posli({ akce: "zrusit-lekci", id: l.id, zruseno: true },
        (j) => {
          const sms = j.sms || {};
          const casti = [`Lekce zrušena, vstup se vrátil ${j.vraceno} účastnicím.`];
          if (sms.odeslano) casti.push(`SMS odešla ${sms.odeslano}×.`);
          if (sms.bezTelefonu) casti.push(`${sms.bezTelefonu} bez telefonu, ozvi se jim sám.`);
          if (sms.chyby && sms.chyby.length) casti.push(`SMS selhala: ${sms.chyby.join(", ")}`);
          casti.push("Nezapomeň do dvou dnů nabídnout náhradní termín.");
          return casti.join(" ");
        }),
    });
  }

  // Smazání účastnice. Stejné z tabulky i ze seznamu na telefonu.
  function smazatUcastnici(u) {
    setConfirm({
      title: "Smazat účastnici?",
      description: `Smaže se ${u.jmeno} i s jejími rezervacemi. Tohle nejde vrátit.`,
      confirmLabel: "Smazat", variant: "danger",
      run: () => posli({ akce: "smazat-ucastnici", id: u.id }, "Účastnice smazána."),
    });
  }

  // Nová účastnice a uložení upravené. Používá formulář v obou rozvrženích.
  async function ulozNovouUcastnici() {
    const j = await posli({ akce: "ulozit-ucastnici", bootcamp_id: beh.id, fields: novaUcastnice }, "Účastnice přidána.");
    if (j) setNovaUcastnice(null);
  }
  async function ulozUpravenou(id) {
    const j = await posli({ akce: "ulozit-ucastnici", id, fields: editUcastnice }, "Uloženo.");
    if (j) setEditUcastnice(null);
  }

  // Souhrny do sbalených bloků nastavení (16.9.2026). Ať se kvůli kontrole
  // nemusí nic rozbalovat: v jedné řádce je to, co se v bloku nejčastěji hlídá.
  const n = nastaveni || {};
  const trenerJmeno = (treneri.find((t) => String(t.id) === String(n.trener_id)) || {}).jmeno || "";
  const souhrnLokalita = [n.nazev, trenerJmeno, n.kapacita ? `kapacita ${n.kapacita}` : null]
    .filter(Boolean).join(", ");
  const dnyRozvrhu = [...new Set((n.rozvrh || []).map((r) => Number(r.den)))]
    .sort((a, b) => a - b)
    .map((d) => (DNY.find((x) => x.id === d) || {}).zkratka)
    .filter(Boolean);
  const souhrnRozvrh = [
    dnyRozvrhu.length ? dnyRozvrhu.join(" a ") : "bez rozvrhu",
    (n.mista || []).length > 1 ? `${(n.mista || []).length} místa setkání` : null,
    n.bez_konce || !n.datum_do ? "jede pořád" : `do ${den(n.datum_do)}`,
  ].filter(Boolean).join(", ");
  const souhrnPauzy = (n.pauzy || []).length
    ? (n.pauzy || []).map((p) => `${p.duvod ? `${p.duvod}, ` : ""}${den(p.od)} až ${den(p.do)}`).join(" · ")
    : "žádná pauza, cvičí se pořád";
  const ceny = (n.tarify || []).map((t) => t.cena).filter(Boolean);
  const souhrnProdej = [
    ceny.length ? `${ceny.join(" a ")} Kč` : "bez tarifů",
    `platnost ${n.platnost_dnu == null ? 60 : n.platnost_dnu} dní`,
    n.fapi_form_id ? "formulář nastavený" : "bez prodejního formuláře",
  ].join(", ");
  const souhrnProvoz = [
    n.prihlaseni_do ? `uzávěrka ${n.prihlaseni_do}` : "bez uzávěrky",
    `storno ${n.storno_hodin || 0} h`,
    n.telegram_chat_id ? "vlastní Telegram" : "společný Telegram",
    beh && beh.archivovana ? "archivovaná" : (STAV_BEHU[stavZDat(n)] || STAV_BEHU.pripravovany).label.toLowerCase(),
  ].join(", ");

  // 3. 11. 2026: řádky lekcí jedné skupiny (měsíce). Nadcházející a proběhlé
  // lekce s rozbalováním po letech a měsících skládá LekcePoMesicich.
  const vypisLekci = (lekce) => (
  uzka ? (
                  /* 14.9.2026: na telefonu jeden řádek na lekci. Dřív se řádek
                     tabulky rozpadl na blok a jedna lekce brala přes dvě stě bodů,
                     takže se k dnešku rolovalo přes celou obrazovku. */
                  <div className="-mx-1 divide-y divide-slate-100">
                    {lekce.map((l) => {
                      const st = stavDochazky(l);
                      const jeDnes = String(l.datum).substring(0, 10) === dnesIso;
                      const otevrena = rozbalenaLekce === l.id;
                      return (
                        <div key={l.id}>
                          <button
                            type="button"
                            onClick={() => { setRozbalenaLekce(otevrena ? null : l.id); setPridavana(""); }}
                            aria-expanded={otevrena}
                            className={`flex w-full items-center gap-2.5 rounded-xl px-2 py-2.5 text-left ${l.zruseno ? "opacity-50" : ""} ${jeDnes ? "bg-brand-light/60" : ""}`}
                          >
                            <span className="w-[104px] shrink-0">
                              <span className="block text-base font-bold leading-tight text-slate-900">{denVTydnu(l.datum)} {den(l.datum)}</span>
                              <span className="block text-[13px] tabular-nums text-slate-500">{l.cas_od}</span>
                            </span>
                            {l.zruseno ? (
                              <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[13px] font-semibold text-slate-500">zrušená</span>
                            ) : (
                              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[13px] font-semibold tabular-nums ${
                                st.hotovo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                                {l.obsazeno} z {l.kapacita}
                              </span>
                            )}
                            <span className="flex-1" />
                            {jeDnes && <span className="shrink-0 rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-white">dnes</span>}
                            <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevrena ? "" : "-rotate-90"}`} />
                          </button>
                          {otevrena && (
                            <div className="space-y-2 pb-3">
                              {dochazkaLekce(l)}
                              {(muzeLekce || smiNastaveni) && (
                                <div className="flex flex-wrap gap-1.5 px-3">
                                  {muzeLekce && (
                                    <button type="button" disabled={busy} onClick={() => zrusitLekci(l)}
                                      className={l.zruseno ? PILL_NEUTRAL : PILL_WARN}>
                                      {l.zruseno ? "Obnovit lekci" : "Zrušit lekci"}
                                    </button>
                                  )}
                                  {smiNastaveni && Number(l.obsazeno) === 0 && (
                                    <button type="button" disabled={busy} onClick={() => smazatLekci(l)}
                                      className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1.5 text-[13px] text-slate-500">
                                      Smazat termín
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="sm:overflow-x-auto">
                    {/* Na mobilu se radky skladaji pod sebe jako karty, na siroku zustava tabulka. */}
                    <table className="block sm:table w-full sm:min-w-[560px] text-[15px]">
                      <thead className="hidden sm:table-header-group">
                        <tr className="text-left text-slate-500 border-b border-slate-200">
                          <th className="py-2.5 pr-3 font-medium">Datum</th>
                          <th className="py-2.5 pr-3 font-medium">Čas</th>
                          <th className="py-2.5 pr-3 font-medium">Trenér</th>
                          <th className="py-2.5 pr-3 font-medium">Obsazeno</th>
                          <th className="py-2.5 pr-3 font-medium">Stav</th>
                          <th className="py-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody className="block sm:table-row-group">
                        {lekce.map((l) => (
                          <React.Fragment key={l.id}>
                          <tr className={`block sm:table-row mb-2 sm:mb-0 rounded-xl sm:rounded-none border border-slate-200 sm:border-0 sm:border-b sm:border-slate-100 p-3 sm:p-0 ${l.zruseno ? "opacity-50" : ""} ${rozbalenaLekce === l.id ? "bg-slate-50" : ""}`}>
                            <td className="block sm:table-cell py-0 sm:py-3 pr-0 sm:pr-3 whitespace-nowrap">
                              <button type="button"
                                onClick={() => { setRozbalenaLekce(rozbalenaLekce === l.id ? null : l.id); setPridavana(""); }}
                                className="inline-flex items-center gap-1.5 font-medium text-slate-800 hover:text-brand-700">
                                {rozbalenaLekce === l.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                {denVTydnu(l.datum)} {den(l.datum)}
                              </button>
                              {String(l.datum).substring(0, 10) === dnesIso && <span className="ml-2 rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-white">dnes</span>}
                            </td>
                            <td className="block sm:table-cell py-0.5 sm:py-3 pr-0 sm:pr-3 whitespace-nowrap tabular-nums text-slate-600">{l.cas_od}{l.cas_do ? ` až ${l.cas_do}` : ""}</td>
                            <td className="block sm:table-cell py-0.5 sm:py-2 pr-0 sm:pr-3 text-slate-600">
                              <span className="sm:hidden text-slate-400">Trenér: </span>
                              {muzeLekce ? (
                                <select
                                  value={l.trener_id || ""}
                                  disabled={busy}
                                  onChange={(e) => zmenTrenera(l, e.target.value)}
                                  aria-label={`Trenér lekce ${den(l.datum)}`}
                                  className="min-w-[8.5rem] rounded-lg border border-slate-200 bg-white px-2 py-1 text-[13px] text-slate-700 disabled:opacity-50"
                                >
                                  <option value="">bez trenéra</option>
                                  {treneri.map((t) => <option key={t.id} value={t.id}>{t.jmeno}</option>)}
                                </select>
                              ) : (l.trener_name || "—")}
                            </td>
                            <td className="block sm:table-cell py-0.5 sm:py-3 pr-0 sm:pr-3 tabular-nums text-slate-600"><span className="sm:hidden text-slate-400">Obsazeno: </span>{l.obsazeno} / {l.kapacita}</td>
                            <td className="block sm:table-cell py-1.5 sm:py-2 pr-0 sm:pr-3">
                              {l.zruseno
                                ? <span className="px-2.5 py-1 rounded-full text-[12.5px] bg-slate-100 text-slate-500">Zrušená</span>
                                : <span className="px-2.5 py-1 rounded-full text-[12.5px] bg-emerald-100 text-emerald-700">Platí</span>}
                            </td>
                            <td className="block sm:table-cell py-0 sm:py-3 text-left sm:text-right whitespace-nowrap">
                              <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
                                {muzeLekce && (
                                  <button type="button" disabled={busy}
                                    onClick={() => zrusitLekci(l)}
                                    className={l.zruseno ? PILL_NEUTRAL : PILL_WARN}>
                                    {l.zruseno ? "Obnovit" : "Zrušit lekci"}
                                  </button>
                                )}
                                {smiNastaveni && Number(l.obsazeno) === 0 && (
                                  <button type="button" disabled={busy} onClick={() => smazatLekci(l)}
                                    title="Smazat termín, nikdo na něj není přihlášený"
                                    className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-[12.5px] text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600">
                                    Smazat
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {rozbalenaLekce === l.id && (
                            <tr className="block sm:table-row bg-slate-50 rounded-xl sm:rounded-none mb-2 sm:mb-0">
                              <td colSpan={6} className="block sm:table-cell px-0 pb-3">
                                {dochazkaLekce(l)}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
  );

  return (
    <Obal fullPage={fullPage} onClose={onClose} onZalozka={doZalozky}>
      <GradientHeader
        akce={[
          ...(fullPage ? [{ klic: "zpet", ikona: ArrowLeft, popis: "Zpět na Bootcampy", onClick: () => navigate("/bootcampy") }] : []),
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: () => refetch(true), disabled: refreshing },
        ]}
        title={beh.nazev}
        icon={Flame}
        loading={refreshing}
      />

      {beh.archivovana && (
        <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          <Archive className="h-4 w-4 shrink-0 text-slate-400" />
          <span>
            <b className="text-slate-800">Archivovaná lokalita.</b> Nejede, nedoplňují se jí lekce a faktury se do ní
            nepárují. Všechno v ní zůstává, v nastavení dole ji jde vrátit zpátky.
          </span>
        </div>
      )}
      {!beh.archivovana && beh.nespusteno && (
        <div className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <PauseCircle className="h-4 w-4 shrink-0 text-amber-600" />
          <span>
            <b className="text-amber-900">Zatím nespuštěná lokalita.</b> Termíny se nezakládají, zprávy nechodí
            a na webu je skrytá. Spustit ji jde v nastavení, v části Provoz.
          </span>
        </div>
      )}
      {chyba && <ErrorNote>{chyba}</ErrorNote>}
      {hlaska && <Hlaska ton="ok" title={hlaska} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Dlazdice popisek="Účastnic" hodnota={ucastnice.length} />
        <Dlazdice popisek="Zaplaceno" hodnota={zaplacenych} ton="ok" />
        <Dlazdice popisek="Lekcí" hodnota={lekce.filter((l) => !l.zruseno).length} ton="info" />
        <Dlazdice popisek="Kapacita lekce" hodnota={beh.kapacita} ton="zlata" />
      </div>

      {/* 14.9.2026: na telefonu je nahoře dnešní lekce. Trenér stránku otevírá
          skoro vždycky kvůli ní a dřív se k ní proklikával přes čtyři sekce.
          Na počítači se nekreslí, tam je celý rozvrh vidět najednou. */}
      {uzka && lekceDnes && (
        <KartaDnesniLekce
          lekce={lekceDnes.lekce}
          jeDnes={lekceDnes.jeDnes}
          stav={stavDochazky(lekceDnes.lekce)}
          muzeZapisovat={muzeLekce}
          otevreno={otevrenaDnes}
          onToggle={() => { setOtevrenaDnes((x) => !x); setPridavana(""); }}
        >
          {dochazkaLekce(lekceDnes.lekce)}
        </KartaDnesniLekce>
      )}

      {/* ── Nastavení lokality ─────────────────────────────────────────── */}
      {smiNastaveni && (
      <div ref={refs.nastaveni} className="scroll-mt-24">
        <Card
          lista
          title="Nastavení lokality"
          onHeaderClick={() => prepni("nastaveni")}
          action={<Prepinac otevreno={otevreno.nastaveni} onClick={() => prepni("nastaveni")} />}
        >
          {otevreno.nastaveni && nastaveni && (
            <>
              {/* Čtyři bloky podle toho, kdy do nich člověk sáhne (16.9.2026,
                  odsouhlasený mockup). Akce sedí u toho, čeho se týkají:
                  generování lekcí a přepočet míst k rozvrhu, přepočet platností
                  k prodeji. Z lišt karet Lekce a Účastnice proto zmizely. */}
              <div className="space-y-3">
                <Blok
                  ikona={MapPin}
                  nazev="Lokalita"
                  popis="Jak se jmenuje a kde je. Mění se výjimečně."
                  souhrn={souhrnLokalita}
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <Pole label="Název lokality" hint="Jde i do SMS a do klientské aplikace.">
                      <input className="field" value={nastaveni.nazev} onChange={(e) => setNastaveni({ ...nastaveni, nazev: e.target.value })} />
                    </Pole>
                    <Pole label="Adresa" hint="Kde přesně se sraz koná.">
                      <input className="field" value={nastaveni.adresa || ""} onChange={(e) => setNastaveni({ ...nastaveni, adresa: e.target.value })} placeholder="Toužimská 732, Praha 9" />
                    </Pole>
                    <Pole label="Trenér">
                      <select className="field" value={nastaveni.trener_id} onChange={(e) => setNastaveni({ ...nastaveni, trener_id: e.target.value })}>
                        <option value="">Nevybráno</option>
                        {treneri.map((t) => <option key={t.id} value={t.id}>{t.jmeno}</option>)}
                      </select>
                    </Pole>
                    <Pole label="Kapacita lekce"><input type="number" className="field" value={nastaveni.kapacita} onChange={(e) => setNastaveni({ ...nastaveni, kapacita: e.target.value })} /></Pole>
                    <div className="sm:col-span-2">
                      <Pole label="Poznámka" hint="Interní, klientky ji nevidí.">
                        <input className="field" value={nastaveni.poznamka || ""} onChange={(e) => setNastaveni({ ...nastaveni, poznamka: e.target.value })} placeholder="Od listopadu do března se cvičí v hale" />
                      </Pole>
                    </div>
                  </div>
                </Blok>

                <Blok
                  ikona={Clock}
                  nazev="Kdy a kde se cvičí"
                  popis="Rozvrh a místa setkání. Z tohohle se zakládají lekce."
                  souhrn={souhrnRozvrh}
                  vychoziOtevreno
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <Pole label="Od"><input type="date" className="field" value={nastaveni.datum_od} onChange={(e) => setNastaveni({ ...nastaveni, datum_od: e.target.value })} /></Pole>
                    <PoleDatumDo
                      hodnota={nastaveni.datum_do}
                      bezKonce={nastaveni.bez_konce}
                      onZmena={(v) => setNastaveni({ ...nastaveni, datum_do: v })}
                      onPrepni={(v) => setNastaveni({ ...nastaveni, bez_konce: v, datum_do: v ? "" : nastaveni.datum_do })}
                    />
                  </div>

                  <RozvrhEditor rozvrh={nastaveni.rozvrh || []} onZmena={(v) => setNastaveni({ ...nastaveni, rozvrh: v })} />

                  <MistaEditor mista={nastaveni.mista || []} misto={nastaveni.nazev}
                    onZmena={(v) => setNastaveni({ ...nastaveni, mista: v })} />

                  <p className="text-xs text-slate-500">
                    Lekce se z rozvrhu založí samy hned po uložení a pak se doplňují každou hodinu,
                    jakmile jich dopředu zbývá míň než 90 dní. Místo setkání se u budoucích lekcí
                    přepíše taky samo.
                  </p>
                </Blok>

                <Blok
                  ikona={CalendarOff}
                  nazev="Pauzy a volna"
                  popis="Dny, kdy se necvičí. Lekce se v nich nezaloží, ženám se o ně prodlouží platnost permanentky."
                  souhrn={souhrnPauzy}
                  vychoziOtevreno
                >
                  <PauzyEditor pauzy={nastaveni.pauzy || []} onZmena={(v) => setNastaveni({ ...nastaveni, pauzy: v })} />
                  <p className="text-xs text-slate-500">
                    Pauza se váže na konkrétní data. Jakmile ji uložíš, samo se stane tohle: budoucí
                    lekce v těch dnech se smažou, a všem klientkám s uhrazenou fakturou se o dny pauzy
                    prodlouží platnost vstupů. Lekci, na kterou už je někdo přihlášený, smazat nejde,
                    tu zruš v seznamu Lekce, ať ženám odejde SMS.
                  </p>
                </Blok>

                <Blok
                  ikona={CreditCard}
                  nazev="Prodej a platnost"
                  popis="Co se prodává, jak se to páruje z faktur a jak dlouho permanentka platí."
                  souhrn={souhrnProdej}
                >
                  <div>
                    <div className="mb-1.5 text-sm font-medium text-slate-700">
                      Tarify <span className="font-normal text-slate-500">(prázdný počet vstupů znamená bez omezení, prázdná platnost bere platnost běhu)</span>
                    </div>
                    <div className="space-y-2">
                      {(nastaveni.tarify || []).map((t, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                          {["nazev", "vzor", "vstupy", "cena", "platnost_dnu"].map((k) => (
                            <input key={k} className="field" value={t[k] == null ? "" : t[k]}
                              placeholder={{ nazev: "Název", vzor: "Vzor v položce faktury", vstupy: "Vstupů", cena: "Cena Kč", platnost_dnu: "Platnost dní" }[k]}
                              onChange={(e) => {
                                const n = [...nastaveni.tarify]; n[i] = { ...t, [k]: e.target.value };
                                setNastaveni({ ...nastaveni, tarify: n });
                              }} />
                          ))}
                        </div>
                      ))}
                      <button type="button" className="text-sm text-brand-700 hover:underline"
                        onClick={() => setNastaveni({ ...nastaveni, tarify: [...(nastaveni.tarify || []), { nazev: "", vzor: "", vstupy: "", cena: "", platnost_dnu: "" }] })}>
                        Přidat tarif
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <Pole label="Vzor položky faktury"><input className="field" value={nastaveni.fapi_pattern} onChange={(e) => setNastaveni({ ...nastaveni, fapi_pattern: e.target.value })} /></Pole>
                    <Pole label="ID prodejního formuláře FAPI" hint="Podle něj aplikace nabídne dokoupení vstupů právě do téhle lokality.">
                      <input className="field" value={nastaveni.fapi_form_id || ""}
                        onChange={(e) => setNastaveni({ ...nastaveni, fapi_form_id: e.target.value })} />
                    </Pole>
                    <Pole label="Platnost permanentky (dnů)" hint="Od data platby. Dny pauzy se přičtou navíc. 0 znamená bez omezení.">
                      <input type="number" className="field" value={nastaveni.platnost_dnu == null ? 60 : nastaveni.platnost_dnu}
                        onChange={(e) => setNastaveni({ ...nastaveni, platnost_dnu: e.target.value })} />
                    </Pole>
                    <Pole label="Prodej od" hint="Od tohohle data se do lokality párují faktury. Prázdné znamená 120 dní před začátkem.">
                      <input type="date" className="field" value={nastaveni.prodej_od || ""} onChange={(e) => setNastaveni({ ...nastaveni, prodej_od: e.target.value })} />
                    </Pole>
                    <Pole label="Přihlášky do" hint="Do kdy jde koupit. Prázdné znamená bez omezení.">
                      <input type="date" className="field" value={nastaveni.prihlasky_do} onChange={(e) => setNastaveni({ ...nastaveni, prihlasky_do: e.target.value })} />
                    </Pole>
                  </div>

                  <p className="text-xs text-slate-500">
                    Faktury se načítají samy každou hodinu, platnost se nastaví při zaplacení
                    a po uložení pauzy se všem prodlouží. Ručně tu není co spouštět.
                  </p>
                </Blok>

                <Blok
                  ikona={SlidersHorizontal}
                  nazev="Provoz"
                  popis="Pravidla přihlašování a rušení, telegramová skupina, stav lokality."
                  souhrn={souhrnProvoz}
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <Pole label="Přihlášení do (hodina)"><input type="time" className="field" value={nastaveni.prihlaseni_do || ""} onChange={(e) => setNastaveni({ ...nastaveni, prihlaseni_do: e.target.value })} /></Pole>
                    <Pole label="Přihlášení do (dnů předem)">
                      <select className="field" value={String(nastaveni.prihlaseni_dnu_predem == null ? 0 : nastaveni.prihlaseni_dnu_predem)}
                        onChange={(e) => setNastaveni({ ...nastaveni, prihlaseni_dnu_predem: parseInt(e.target.value, 10) })}>
                        <option value="0">v den lekce</option>
                        <option value="1">den předem</option>
                        <option value="2">2 dny předem</option>
                        <option value="3">3 dny předem</option>
                        <option value="7">týden předem</option>
                      </select>
                    </Pole>
                    <Pole label="Storno lhůta (hodin)"><input type="number" className="field" value={nastaveni.storno_hodin} onChange={(e) => setNastaveni({ ...nastaveni, storno_hodin: e.target.value })} /></Pole>
                    <Pole label="Telegram skupina (chat ID)" hint="Kam chodí zprávy o zrušené lekci. Prázdné použije společnou skupinu.">
                      <input className="field" value={nastaveni.telegram_chat_id || ""}
                        onChange={(e) => setNastaveni({ ...nastaveni, telegram_chat_id: e.target.value })} placeholder="-1002…" />
                    </Pole>
                  </div>

                  <div>
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Stav</span>
                    <div className="flex flex-wrap items-center gap-2.5">
                      {(() => {
                        const auto = stavZDat(nastaveni);
                        const m = STAV_BEHU[auto] || STAV_BEHU.pripravovany;
                        return (
                          <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border ${m.cls}`}>
                            <m.Ikona className="w-4 h-4" /> {m.label}
                          </span>
                        );
                      })()}
                      {/* Nespuštěná a předčasně ukončená se vylučují. U nespuštěné
                          se ukončení neukazuje, místo něj je dole Spustit lokalitu. */}
                      {smiNastaveni && !nastaveni.nespusteno && (
                        <button type="button"
                          onClick={() => setNastaveni({ ...nastaveni, ukoncen_rucne: !nastaveni.ukoncen_rucne, nespusteno: false })}
                          className={`inline-flex items-center gap-1.5 min-h-[38px] px-3 py-1.5 rounded-xl border text-sm font-medium transition ${
                            nastaveni.ukoncen_rucne
                              ? "border-brand-300 bg-brand-50 text-brand-700 hover:bg-brand-100"
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                          <Ban className="w-4 h-4" />
                          {nastaveni.ukoncen_rucne ? "Zrušit předčasné ukončení" : "Ukončit předčasně"}
                        </button>
                      )}
                    </div>
                    <span className="mt-1.5 block text-xs text-slate-500">
                      {nastaveni.nespusteno
                        ? "Stav drží ručně, dokud lokalitu nespustíš. Pak se zase počítá z dat."
                        : <>
                            Stav se počítá z dat: v provozu od {den(nastaveni.datum_od)}
                            {nastaveni.datum_do ? ", ukončená den po poslední lekci" : ", bez konce se sama neukončí"}.
                            Ruční ukončení použij, jen když se lokalita zavře dřív.
                          </>}
                    </span>
                  </div>

                  {/* Zatím nespuštěno (3. 11. 2026, zadání Ládi): Letná se teď
                      nespouští, ale není ani ukončená. Uložená nespuštěná
                      lokalita má zvýrazněné Spustit lokalitu, které platí hned.
                      Jinak je tu přepínač, který se uloží s nastavením. */}
                  {!beh.archivovana && (beh.nespusteno ? (smiNastaveni && (
                    <Hlaska
                      ton="pozor"
                      title="Lokalita zatím není spuštěná"
                      akce={
                        <button type="button" disabled={busy}
                          onClick={() => setConfirm({
                            title: `Spustit lokalitu ${beh.nazev}?`,
                            description: "Hned se založí termíny podle rozvrhu a lokalita se začne chovat jako běžící. "
                              + "Na webu zůstane skrytá, dokud ji tam nezobrazíš.",
                            confirmLabel: "Spustit lokalitu", variant: "primary",
                            run: async () => {
                              const j = await posli({ akce: "spustit-lokalitu", id: beh.id }, (r) => {
                                const casti = [`${beh.nazev} je spuštěná.`];
                                if (r.bezRozvrhu) casti.push("Chybí rozvrh, termíny se založí až po jeho doplnění.");
                                else if (r.vytvorenoLekci) casti.push(`Založeno ${r.vytvorenoLekci} termínů.`);
                                else casti.push("Termíny podle rozvrhu už jsou založené.");
                                if (r.skrytaNaWebu) casti.push("Na webu zůstává skrytá.");
                                return casti.join(" ");
                              });
                              // Formulář drží svou kopii nastavení. Bez tohohle by
                              // další Uložit nastavení lokalitu zase vypnulo.
                              if (j) setNastaveni((x) => (x ? { ...x, nespusteno: false, ukoncen_rucne: false } : x));
                            },
                          })}
                          className="btn btn-primary w-full sm:w-auto">
                          <Play className="w-4 h-4" /> Spustit lokalitu
                        </button>
                      }
                    >
                      Termíny se nezakládají, zprávy nechodí. Po spuštění se termíny hned založí podle rozvrhu.
                    </Hlaska>
                  )) : (jeOwner && (
                    <div>
                      <Zapinatko
                        zapnuto={!!nastaveni.nespusteno}
                        onZmena={(v) => setNastaveni({ ...nastaveni, nespusteno: v, ukoncen_rucne: v ? false : nastaveni.ukoncen_rucne })}
                        popisek="Zatím nespouštět"
                        ikona={PauseCircle}
                      />
                      <span className="mt-1.5 block text-xs text-slate-500">
                        Termíny se nezakládají, zprávy nechodí. Po spuštění se termíny hned založí podle rozvrhu.
                        {nastaveni.nespusteno && " Projeví se po uložení nastavení."}
                      </span>
                    </div>
                  )))}
                </Blok>
              </div>

              {jeManager && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" disabled={busy}
                    onClick={async () => {
                      // Nespuštění se posílá jen při změně oproti načtené
                      // lokalitě (audit 3. 11. 2026). Jinak pole chybí a server
                      // drží hodnotu z databáze, ať stará kopie formuláře nic
                      // nepřepíše.
                      const fields = { ...nastaveni, datum_do: nastaveni.bez_konce ? "" : nastaveni.datum_do };
                      const nacteno = beh.nespusteno === true;
                      if (!!nastaveni.nespusteno === nacteno) delete fields.nespusteno;
                      else fields.nespusteno = !!nastaveni.nespusteno;
                      // Přepnutí běžící lokality na nespuštěno skryje její
                      // termíny z kalendáře trenéra, proto napřed potvrzení.
                      if (fields.nespusteno === true) {
                        const budouci = lekce.filter((l) => !l.zruseno && String(l.datum).substring(0, 10) >= dnesIso).length;
                        const ano = await confirmDialog({
                          title: `Přepnout ${beh.nazev} na Zatím nespuštěno?`,
                          description: (budouci
                            ? `${budouci} ${budouci === 1 ? "budoucí lekce se přestane" : budouci < 5 ? "budoucí lekce se přestanou" : "budoucích lekcí se přestane"} ukazovat v kalendáři trenéra. `
                            : "Lokalita zatím nemá žádné budoucí lekce. ")
                            + "Nové termíny se nebudou zakládat, zprávy nechodí a na webu bude lokalita skrytá.",
                          confirmLabel: "Přepnout na nespuštěno",
                          variant: "danger",
                        });
                        if (!ano) return;
                      }
                      posli({
                      akce: "ulozit-bootcamp", id: beh.id,
                      fields,
                    }, (j) => {
                      // Po uložení server sám dorovná lekce, místa i platnosti.
                      // Tady se jen řekne, co se stalo, ať to není tichá magie.
                      const s2 = j.srovnani || {};
                      const casti = ["Nastavení uloženo."];
                      if (s2.vytvorenoLekci) casti.push(`Založeno ${s2.vytvorenoLekci} lekcí.`);
                      if (s2.smazanoVPauze) casti.push(`Smazáno ${s2.smazanoVPauze} lekcí v pauze.`);
                      if (s2.zmenenoMist) casti.push(`Přepsáno místo u ${s2.zmenenoMist} lekcí.`);
                      if (s2.doplnenoTreneru) casti.push(`Doplněn trenér u ${s2.doplnenoTreneru} lekcí.`);
                      if (s2.prodlouzenoPlatnosti) casti.push(`Prodloužena platnost ${s2.prodlouzenoPlatnosti} klientkám.`);
                      if (s2.nespusteno && s2.budoucichLekci) casti.push(`Lokalita je zatím nespuštěná, nové termíny se nezakládají. Zůstává ${s2.budoucichLekci} dřív založených lekcí, zruš je v seznamu Lekce.`);
                      if (s2.vPauze && s2.vPauze.length) {
                        casti.push(`V pauze zůstávají lekce s rezervací: ${s2.vPauze.map((x) => `${den(x.datum)} ${x.cas_od}`).join(", ")}. Zruš je v seznamu Lekce, ať ženám přijde SMS.`);
                      }
                      return casti.join(" ");
                    });
                    }}
                    className="btn btn-primary">
                    <Save className="w-4 h-4" /> Uložit nastavení
                  </button>
                  <button type="button"
                    onClick={() => setPresun(presun ? null : { do_bootcamp_id: "", platnost_do: "" })}
                    className="btn btn-secondary">
                    <Users className="w-4 h-4" /> {presun ? "Zavřít přesun" : "Přesunout klientky"}
                  </button>
                  {/* Lokalita, kterou prosly klientky, se nemaze. Jejich dochazka,
                      faktury a komentare jsou historie firmy, a ta se archivuje.
                      Mazat jde jen prazdna lokalita a jen ownerovi (16.9.2026). */}
                  {beh.archivovana ? (
                    <button type="button" disabled={busy}
                      onClick={() => posli({ akce: "archivovat-bootcamp", id: beh.id, archivovana: false },
                        "Lokalita je zpátky mezi aktivními.")}
                      className="btn btn-secondary">
                      <ArchiveRestore className="w-4 h-4" /> Vrátit z archivu
                    </button>
                  ) : (
                    <button type="button" disabled={busy}
                      onClick={() => setConfirm({
                        title: "Archivovat lokalitu?",
                        description: "Zmizí ze seznamu lokalit, přestane si sama doplňovat lekce a nebudou se do ní párovat faktury. "
                          + "Klientky, docházka, faktury i komentáře zůstanou a kdykoli ji jde vrátit zpátky.",
                        confirmLabel: "Archivovat", variant: "primary",
                        run: async () => { await posli({ akce: "archivovat-bootcamp", id: beh.id, archivovana: true }, "Lokalita archivována."); onChanged?.(); },
                      })}
                      className="btn btn-secondary">
                      <Archive className="w-4 h-4" /> Archivovat lokalitu
                    </button>
                  )}
                  {jeOwner && ucastnice.length === 0 && (
                    <button type="button"
                      onClick={() => setConfirm({
                        title: "Smazat lokalitu?",
                        description: "Smaže se lokalita i všechny její lekce. Tohle nejde vrátit. "
                          + "Jde to jen proto, že v ní nikdy nebyla žádná klientka.",
                        confirmLabel: "Smazat", variant: "danger",
                        run: async () => { await posli({ akce: "smazat-bootcamp", id: beh.id }); onChanged?.(); onClose?.(); },
                      })}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-rose-200 text-rose-700 text-sm hover:bg-rose-50">
                      <Trash2 className="w-4 h-4" /> Smazat lokalitu
                    </button>
                  )}
                </div>
              )}

              {presun && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-medium text-slate-700">Přesunout klientky do jiné lokality</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    Klientky i jejich budoucí rezervace přejdou do vybrané lokality. Termíny se páruje podle data a času,
                    takže tam napřed musí být vygenerované lekce. Počet vstupů zůstává, odchozené tréninky se počítají dál.
                    Proběhlé lekce se i s docházkou přestěhují, takže tady po sloučení nezůstane viset prázdná lokalita
                    jen kvůli historii. Tahle lokalita se pak ukončí.
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Pole label="Do lokality">
                      <select className="field" value={presun.do_bootcamp_id}
                        onChange={(e) => setPresun({ ...presun, do_bootcamp_id: e.target.value })}>
                        <option value="">Nevybráno</option>
                        {((seznamLokalit && seznamLokalit.bootcampy) || [])
                          .filter((b) => Number(b.id) !== Number(beh.id))
                          .map((b) => <option key={b.id} value={b.id}>{b.nazev}</option>)}
                      </select>
                    </Pole>
                    <Pole label="Vstupy vyčerpat do" hint="Nastaví se všem přesunutým. Prázdné nechá platnost, jakou mají.">
                      <input type="date" className="field" value={presun.platnost_do}
                        onChange={(e) => setPresun({ ...presun, platnost_do: e.target.value })} />
                    </Pole>
                    <div className="flex items-end">
                      <button type="button" disabled={busy || !presun.do_bootcamp_id}
                        className="btn btn-primary"
                        onClick={() => posli(
                          { akce: "presun-klientek", z_bootcamp_id: beh.id, do_bootcamp_id: presun.do_bootcamp_id, platnost_do: presun.platnost_do, nahled: true },
                          (j) => {
                            const p = j.plan || {};
                            if (j.blokovano) return `${j.blokovano} Chybí termíny: ${(p.rezervaceBezProtejsku || []).map((x) => `${den(x.datum)} ${x.cas_od}`).join(", ")}`;
                            setConfirm({
                              title: "Přesunout klientky?",
                              description: `Do lokality ${p.cil} přejde ${p.klientek} klientek a ${p.rezervaciKPrepojeni} budoucích rezervací. `
                                + `Z téhle lokality se uklidí ${p.budoucichLekciZdroje} budoucích lekcí, zbylé lekce i s docházkou se přestěhují do ${p.cil} a lokalita se ukončí. `
                                + (p.platnost_do ? `Všem se nastaví platnost do ${den(p.platnost_do)}. ` : "")
                                + "Tohle nejde vrátit.",
                              confirmLabel: "Přesunout", variant: "primary",
                              run: async () => {
                                await posli(
                                  { akce: "presun-klientek", z_bootcamp_id: beh.id, do_bootcamp_id: presun.do_bootcamp_id, platnost_do: presun.platnost_do },
                                  (v) => `Přesunuto ${v.presunuto} klientek, přepojeno ${v.prepojeno} rezervací, přestěhováno ${v.prestehovanoLekci} lekcí, smazáno ${v.smazanoLekci} prázdných.`,
                                );
                                setPresun(null);
                                onChanged?.();
                              },
                            });
                            return `Náhled: ${p.klientek} klientek, ${p.rezervaciKPrepojeni} rezervací.`;
                          },
                        )}>
                        Náhled přesunu
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      )}

      {/* ── Lekce ──────────────────────────────────────────────────── */}
      <div ref={refs.lekce} className="scroll-mt-24">
        <Card
          lista
          title="Lekce"
          odznak={lekce.length}
          onHeaderClick={() => prepni("lekce")}
          action={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {muzeLekce && (
                <button type="button"
                  onClick={() => { if (!otevreno.lekce) prepni("lekce"); setNovaLekce({ datum: "", cas_od: beh.cas_od || "", cas_do: beh.cas_do || "", trener_id: beh.trener_id || "", poznamka: "", sms: true }); }}
                  title="Náhradní lekce za zrušenou, účastnice se na ni hlásí jako na ostatní"
                  aria-label="Přidat náhradní termín"
                  className={AKCE_LISTY_IKONA}>
                  <Plus className="w-5 h-5" />
                </button>
              )}
              <Prepinac otevreno={otevreno.lekce} onClick={() => prepni("lekce")} />
            </div>
          }
        >
          {otevreno.lekce && novaLekce && (
            <FormularLekce
              hodnoty={novaLekce} setHodnoty={setNovaLekce} treneri={treneri} busy={busy}
              onUloz={async () => {
                const { sms, ...fields } = novaLekce;
                const j = await posli(
                  { akce: "ulozit-lekci", bootcamp_id: beh.id, fields, sms: sms === true },
                  (odp) => {
                    if (!odp?.sms) return "Lekce přidána.";
                    const s = odp.sms;
                    const casti = [`Lekce přidána, SMS odešla ${s.odeslano} účastnicím`];
                    if (s.bezTelefonu) casti.push(`${s.bezTelefonu} bez telefonu`);
                    if (s.chyby?.length) casti.push(`${s.chyby.length} se nepodařilo odeslat`);
                    return casti.join(", ") + ".";
                  },
                );
                if (j) setNovaLekce(null);
              }}
              behId={beh.id}
              misto={beh.misto}
              onZrus={() => setNovaLekce(null)}
            />
          )}
          {otevreno.lekce && (
            lekce.length === 0 && !novaLekce
              ? <div className="text-sm text-slate-500">Zatím žádné lekce. Doplň rozvrh v nastavení lokality a ulož ho, termíny se založí samy.</div>
              : lekce.length > 0 && <LekcePoMesicich lekce={lekce} dnesIso={dnesIso} vypis={vypisLekci} />
          )}
        </Card>
      </div>

      {/* ── Účastnice ──────────────────────────────────────────────── */}
      <div ref={refs.ucastnice} className="scroll-mt-24">
        <Card
          lista
          title="Účastnice"
          odznak={ucastnice.length}
          onHeaderClick={() => prepni("ucastnice")}
          action={
            <div className="flex flex-wrap items-center justify-end gap-2">
              {jeManager && (
                <button type="button"
                  onClick={() => setNovaUcastnice({ jmeno: "", email: "", telefon: "", tarif: "", permice_celkem: "", stav: "prihlasena", klient_id: "", platnost_do: "" })}
                  className={AKCE_LISTY_IKONA} title="Přidat účastnici" aria-label="Přidat účastnici">
                  <Plus className="w-5 h-5" />
                </button>
              )}
              <Prepinac otevreno={otevreno.ucastnice} onClick={() => prepni("ucastnice")} />
            </div>
          }
        >
          {otevreno.ucastnice && (
            <>
              {novaUcastnice && (
                <FormularUcastnice
                  hodnoty={novaUcastnice} setHodnoty={setNovaUcastnice} tarify={beh.tarify || []}
                  onUloz={async () => {
                    const j = await posli({ akce: "ulozit-ucastnici", bootcamp_id: beh.id, fields: novaUcastnice }, "Účastnice přidána.");
                    if (j) setNovaUcastnice(null);
                  }}
                  onZrus={() => setNovaUcastnice(null)} busy={busy}
                />
              )}

              {ucastnice.length === 0 && !novaUcastnice && (
                <div className="text-sm text-slate-500">
                  Zatím nikdo. Účastnice se zakládají samy z FAPI faktur, které odpovídají vzoru položky, nebo je přidáte ručně.
                </div>
              )}

              {uzka ? (
                /* 14.9.2026: na telefonu jeden řádek na ženu. Kontakt, faktura,
                   Upravit a Smazat se ukážou až po klepnutí, dřív visely u každé
                   a jedna žena brala skoro dvě stě bodů. */
                <div className="-mx-1 divide-y divide-slate-100">
                  {ucastnice.map((u) => {
                    const st = STAVY_UCASTI.find((x) => x.id === u.stav) || STAVY_UCASTI[0];
                    const otevrena = otevrenaZena === u.id;
                    const jeEdit = editUcastnice && editUcastnice.id === u.id;
                    return (
                      <div key={u.id}>
                        <button
                          type="button"
                          onClick={() => { setOtevrenaZena(otevrena ? null : u.id); setEditUcastnice(null); }}
                          aria-expanded={otevrena}
                          className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2.5 text-left"
                        >
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${st.tone}`} title={st.label}>
                            <st.Ikona className="h-4 w-4" />
                          </span>
                          <span className={`min-w-0 flex-1 ${u.stav === "zrusena" ? "opacity-60" : ""}`}>
                            <span className="block truncate text-base font-bold leading-tight text-slate-900">{u.jmeno}</span>
                            <span className="block truncate text-[13px] tabular-nums text-slate-500">
                              {u.tarif ? `${u.tarif}, ` : ""}
                              {u.permice_celkem == null ? "bez omezení" : `zbývá ${u.zbyva} z ${u.permice_celkem}`}
                            </span>
                          </span>
                          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevrena ? "" : "-rotate-90"}`} />
                        </button>
                        {otevrena && (
                          <div className="space-y-2 px-2 pb-3">
                            {u.telefon && <RadekKontaktu ikona={Phone} hodnota={u.telefon} href={`tel:${String(u.telefon).replace(/\s/g, "")}`} cislice />}
                            {u.email && <RadekKontaktu ikona={Mail} hodnota={u.email} href={`mailto:${u.email}`} />}
                            {u.klient_jmeno && (
                              <div className="text-[13px] text-slate-500">klientka {u.klient_jmeno}</div>
                            )}
                            {u.faktura && (
                              <div className="rounded-xl bg-slate-50 px-3 py-2.5">
                                <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Faktura</div>
                                <div className="mt-0.5 text-[15px] font-semibold text-slate-800">
                                  {u.faktura.cislo || "bez čísla"}
                                  {u.faktura.castka != null ? `, ${new Intl.NumberFormat("cs-CZ").format(u.faktura.castka)} Kč` : ""}
                                </div>
                                <div className="mt-1">
                                  {u.faktura.zaplacena ? (
                                    <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-[13px] font-semibold text-emerald-700">
                                      {u.faktura.uhrazeno ? `uhrazena ${den(u.faktura.uhrazeno)}` : "uhrazena"}
                                    </span>
                                  ) : (
                                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[13px] font-semibold ${
                                      u.faktura.stav === "Po splatnosti" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                                      {u.faktura.stav === "Po splatnosti"
                                        ? "po splatnosti"
                                        : (u.faktura.splatnost ? `splatnost ${den(u.faktura.splatnost)}` : "nezaplacená")}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            {u.k_vraceni && (
                              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
                                <div className="text-[13px] font-semibold text-rose-800">K vrácení peněz</div>
                                <div className="mt-0.5 text-[13px] leading-snug text-rose-700">{u.k_vraceni_duvod}</div>
                                {jeManager && (
                                  <button type="button"
                                    onClick={() => posli({ akce: "vraceni-vyreseno", id: u.id }, "Označeno jako vyřešené.")}
                                    className="mt-2 flex min-h-[40px] items-center justify-center rounded-xl border border-rose-300 px-3 text-sm font-semibold text-rose-700">
                                    Peníze vrácené
                                  </button>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <button type="button"
                                onClick={() => setEditUcastnice(jeEdit ? null : { ...u, permice_celkem: u.permice_celkem == null ? "" : u.permice_celkem })}
                                className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-brand text-sm font-semibold text-white transition active:scale-[0.99]">
                                <Pencil className="h-4 w-4" /> {jeEdit ? "Zavřít úpravy" : "Upravit"}
                              </button>
                              {jeOwner && (
                                <button type="button" onClick={() => smazatUcastnici(u)}
                                  aria-label="Smazat účastnici" title="Smazat účastnici"
                                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-rose-200 text-rose-500">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            {jeEdit && (
                              <FormularUcastnice
                                hodnoty={editUcastnice} setHodnoty={setEditUcastnice} tarify={beh.tarify || []}
                                onUloz={() => ulozUpravenou(u.id)}
                                onZrus={() => setEditUcastnice(null)} busy={busy}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-2.5 text-xs text-slate-500">
                    {STAVY_UCASTI.map((x) => (
                      <span key={x.id} className="inline-flex items-center gap-1.5">
                        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${x.tone}`}>
                          <x.Ikona className="h-3 w-3" />
                        </span>
                        {x.label.toLowerCase()}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
              <div className="sm:overflow-x-auto">
                <table className="block sm:table w-full sm:min-w-[620px] text-[15px]">
                  <tbody className="block sm:table-row-group">
                    {ucastnice.map((u) => {
                      const st = STAVY_UCASTI.find((s) => s.id === u.stav) || STAVY_UCASTI[0];
                      const jeEdit = editUcastnice && editUcastnice.id === u.id;
                      return (
                        <React.Fragment key={u.id}>
                          <tr className="block sm:table-row mb-2 sm:mb-0 rounded-xl sm:rounded-none border border-slate-200 sm:border-0 sm:border-b sm:border-slate-100 p-3 sm:p-0">
                            <td className="block sm:table-cell py-0 sm:py-3 pr-0 sm:pr-3">
                              <div className="font-medium text-slate-900 text-[15.5px]">{u.jmeno}</div>
                              <div className="text-[13px] text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                                {u.email && <span>{u.email}</span>}
                                {u.telefon && <><Delic /><span className="tabular-nums">{u.telefon}</span></>}
                                {u.klient_jmeno && <><Delic /><span>klientka {u.klient_jmeno}</span></>}
                              </div>
                            </td>
                            <td className="block sm:table-cell py-0.5 sm:py-3 pr-0 sm:pr-3 whitespace-nowrap text-slate-600"><span className="sm:hidden text-slate-400">Tarif: </span>{u.tarif || "—"}</td>
                            <td className="block sm:table-cell py-0.5 sm:py-3 pr-0 sm:pr-3 whitespace-nowrap tabular-nums text-slate-600">
                              {u.permice_celkem == null
                                ? <span className="text-slate-500">bez omezení</span>
                                : <>zbývá <b>{u.zbyva}</b> z {u.permice_celkem}</>}
                            </td>
                            <td className="block sm:table-cell py-1.5 sm:py-3 pr-0 sm:pr-3 whitespace-nowrap">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-[12.5px] font-medium ${st.tone}`}>{st.label}</span>
                              {u.k_vraceni && (
                                <div className="mt-1">
                                  <span className="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[12.5px] font-semibold text-rose-700" title={u.k_vraceni_duvod}>
                                    k vrácení peněz
                                  </span>
                                  {jeManager && (
                                    <button type="button"
                                      onClick={() => posli({ akce: "vraceni-vyreseno", id: u.id }, "Označeno jako vyřešené.")}
                                      className="ml-2 text-[12.5px] font-semibold text-brand-700 underline">
                                      vrácené
                                    </button>
                                  )}
                                </div>
                              )}
                              {u.faktura && (
                                <div className="text-[12px] text-slate-500 mt-1 leading-snug">
                                  {u.faktura.cislo ? `Faktura ${u.faktura.cislo}` : "Faktura"}
                                  {u.faktura.castka != null ? `, ${new Intl.NumberFormat("cs-CZ").format(u.faktura.castka)} Kč` : ""}
                                  {u.faktura.zaplacena
                                    ? (u.faktura.uhrazeno ? `, uhrazena ${den(u.faktura.uhrazeno)}` : ", uhrazena")
                                    : (u.faktura.splatnost ? `, splatnost ${den(u.faktura.splatnost)}` : "")}
                                  {!u.faktura.zaplacena && u.faktura.stav === "Po splatnosti" && (
                                    <span className="text-rose-600 font-medium"> po splatnosti</span>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="block sm:table-cell py-0 sm:py-3 text-left sm:text-right whitespace-nowrap">
                              <div className="inline-flex items-center gap-2">
                              <button type="button" className={PILL_NEUTRAL}
                                onClick={() => setEditUcastnice(jeEdit ? null : { ...u, permice_celkem: u.permice_celkem == null ? "" : u.permice_celkem })}>
                                {jeEdit ? "Zavřít" : "Upravit"}
                              </button>
                              {jeOwner && (
                                <button type="button" className={PILL_DANGER}
                                  onClick={() => smazatUcastnici(u)}>
                                  Smazat
                                </button>
                              )}
                              </div>
                            </td>
                          </tr>
                          {jeEdit && (
                            <tr className="block sm:table-row"><td colSpan={5} className="block sm:table-cell pb-3">
                              <FormularUcastnice
                                hodnoty={editUcastnice} setHodnoty={setEditUcastnice} tarify={beh.tarify || []}
                                onUloz={async () => {
                                  const j = await posli({ akce: "ulozit-ucastnici", id: u.id, fields: editUcastnice }, "Uloženo.");
                                  if (j) setEditUcastnice(null);
                                }}
                                onZrus={() => setEditUcastnice(null)} busy={busy}
                              />
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* ── Docházka ───────────────────────────────────────────────── */}
      {/* Mřížka patnáct žen krát šestnáct termínů se na telefon nevejde, rolovala
          se do stran a prstem se do políčka nedalo trefit. Na telefonu ji nahradí
          docházka u jedné lekce, na počítači zůstává (14.9.2026). */}
      {!uzka && (
      <div ref={refs.dochazka} className="scroll-mt-24">
        <Card
          lista
          title="Docházka a rezervace"
          onHeaderClick={() => prepni("dochazka")}
          action={<Prepinac otevreno={otevreno.dochazka} onClick={() => prepni("dochazka")} />}
        >
          {otevreno.dochazka && (
            ucastnice.length === 0 || lekce.length === 0
              ? <div className="text-sm text-slate-500">Mřížka se ukáže, až budou lekce i účastnice.</div>
              : (
                <>
                  <div className="text-xs text-slate-500 mb-2">Klepnutím na políčko se rezervuje termín nebo označí docházka.</div>
                  <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
                    <table className="text-sm border-separate border-spacing-0">
                      <thead>
                        <tr>
                          <th className="sticky left-0 bg-white z-10 text-left py-2 pr-3 font-medium text-slate-500 border-b border-slate-200">Účastnice</th>
                          {lekce.map((l) => (
                            <th key={l.id} className={`py-2 px-1 font-medium text-slate-500 border-b border-slate-200 whitespace-nowrap ${l.zruseno ? "opacity-40" : ""}`}>
                              <div className="text-[11px]">{denVTydnu(l.datum)}</div>
                              <div className="text-[11px] tabular-nums">{String(l.datum).substring(8, 10)}.{String(l.datum).substring(5, 7)}.</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ucastnice.map((u) => (
                          <tr key={u.id}>
                            <td className="sticky left-0 bg-white z-10 py-1.5 pr-3 border-b border-slate-100 whitespace-nowrap">
                              <span className="font-medium text-slate-800">{u.jmeno}</span>
                              <span className="text-xs text-slate-500 ml-2 tabular-nums">
                                {u.permice_celkem == null ? "∞" : `${u.zbyva}/${u.permice_celkem}`}
                              </span>
                            </td>
                            {lekce.map((l) => {
                              const r = rezMapa.get(`${l.id}:${u.id}`);
                              return (
                                <td key={l.id} className="py-1 px-1 border-b border-slate-100 text-center">
                                  <button type="button" disabled={busy || l.zruseno}
                                    onClick={() => setBunka({ lekce: l, ucastnice: u, rezervace: r || null })}
                                    className="w-7 h-7 rounded-lg inline-flex items-center justify-center hover:bg-slate-100 disabled:opacity-30"
                                    title={`${u.jmeno}, ${den(l.datum)}`}>
                                    <IkonaStavu stav={r ? r.stav : ""} />
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                    <span className="inline-flex items-center gap-1"><IkonaStavu stav="rezervovano" /> rezervováno</span>
                    <span className="inline-flex items-center gap-1"><IkonaStavu stav="dorazila" /> dorazila</span>
                    <span className="inline-flex items-center gap-1"><IkonaStavu stav="nedorazila" /> nedorazila</span>
                    <span className="inline-flex items-center gap-1"><IkonaStavu stav="propadla" /> vstup propadl</span>
                  </div>
                </>
              )
          )}
        </Card>
      </div>
      )}

      {/* Volby nad políčkem mřížky */}
      {bunka && (
        <ModalBunky
          bunka={bunka} busy={busy}
          onZavri={() => setBunka(null)}
          onAkce={async (telo, text) => { await posli(telo, text); setBunka(null); }}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ""}
        description={confirm?.description || ""}
        confirmLabel={confirm?.confirmLabel || "Potvrdit"}
        variant={confirm?.variant || "primary"}
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run?.(); }}
        onCancel={() => setConfirm(null)}
      />
    </Obal>
  );
}

// Lekce po letech a měsících (3. 11. 2026, Láďa). Nahoře nadcházející od dneška,
// pod nimi proběhlé, stejně jako tréninky v detailu klientky. Trenér tak hned
// vidí aktuální termín a nemusí rolovat přes celou historii.
const MESICE = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
function seskupPoMesicich(seznam, vzestupne) {
  const roky = new Map();
  for (const l of seznam) {
    const iso = String(l.datum || "").substring(0, 10);
    const r = Number(iso.substring(0, 4)), m = Number(iso.substring(5, 7)) - 1;
    if (!r || m < 0) continue;
    if (!roky.has(r)) roky.set(r, new Map());
    const mm = roky.get(r);
    if (!mm.has(m)) mm.set(m, []);
    mm.get(m).push(l);
  }
  const smer = (a, b) => (vzestupne ? a - b : b - a);
  return [...roky.entries()].sort((a, b) => smer(a[0], b[0])).map(([rok, mm]) => ({
    rok,
    // Uvnitř měsíce stejný směr jako měsíce (proběhlé od nejnovější).
    mesice: [...mm.entries()].sort((a, b) => smer(a[0], b[0])).map(([m, lekce]) => ({ m, lekce: vzestupne ? lekce : [...lekce].reverse() })),
  }));
}
function sklonujLekce(n) { return n === 1 ? "lekce" : n >= 2 && n <= 4 ? "lekce" : "lekcí"; }
function sklonujMesice(n) { return n === 1 ? "měsíc" : n >= 2 && n <= 4 ? "měsíce" : "měsíců"; }

function MesicLekci({ rok, m, lekce, vypis, vychoziOtevreny }) {
  const [otevreny, setOtevreny] = useState(!!vychoziOtevreny);
  return (
    <div className="mb-2 border-l-2 border-slate-200 pl-3 last:mb-0">
      <button type="button" onClick={() => setOtevreny((o) => !o)} aria-expanded={otevreny}
        className="flex min-h-[44px] w-full items-center gap-2 py-1.5 text-left hover:text-slate-900">
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevreny ? "" : "-rotate-90"}`} />
        <span className="text-base font-semibold text-slate-700">{MESICE[m]} {rok}</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[13px] font-semibold tabular-nums text-slate-600">{lekce.length}</span>
      </button>
      {otevreny && <div className="mt-1">{vypis(lekce)}</div>}
    </div>
  );
}

function RokLekci({ rok, mesice, vypis, vychoziOtevreny, otevritMesic }) {
  const [otevreny, setOtevreny] = useState(!!vychoziOtevreny);
  const celkem = mesice.reduce((a, x) => a + x.lekce.length, 0);
  return (
    <div>
      <button type="button" onClick={() => setOtevreny((o) => !o)} aria-expanded={otevreny}
        className="flex min-h-[44px] w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left transition-colors hover:bg-slate-100">
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${otevreny ? "" : "-rotate-90"}`} />
        <span className="text-base font-bold text-slate-900">Rok {rok}</span>
        <span className="text-[13px] text-slate-600">{mesice.length} {sklonujMesice(mesice.length)}, {celkem} {sklonujLekce(celkem)}</span>
      </button>
      {otevreny && (
        <div className="ml-2 mt-2">
          {mesice.map((x) => (
            <MesicLekci key={`${rok}-${x.m}`} rok={rok} m={x.m} lekce={x.lekce} vypis={vypis}
              vychoziOtevreny={otevritMesic && otevritMesic.rok === rok && otevritMesic.m === x.m} />
          ))}
        </div>
      )}
    </div>
  );
}

function SkupinaLekci({ nadpis, pocet, ton, vychoziOtevrena, prazdno, children }) {
  const [otevrena, setOtevrena] = useState(!!vychoziOtevrena);
  return (
    <div>
      <button type="button" onClick={() => setOtevrena((o) => !o)} aria-expanded={otevrena}
        className="flex min-h-[44px] w-full items-center gap-2 py-1 text-left">
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevrena ? "" : "-rotate-90"}`} />
        <span className="text-[13px] font-bold uppercase tracking-wider text-slate-500">{nadpis}</span>
        <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold tabular-nums ${ton === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{pocet}</span>
      </button>
      {otevrena && (pocet === 0 ? <div className="py-1 pl-6 text-sm text-slate-500">{prazdno}</div> : <div className="mt-1 space-y-3">{children}</div>)}
    </div>
  );
}

function LekcePoMesicich({ lekce, dnesIso, vypis }) {
  const nadchazejici = useMemo(() => seskupPoMesicich(lekce.filter((l) => String(l.datum).substring(0, 10) >= dnesIso), true), [lekce, dnesIso]);
  const probehle = useMemo(() => seskupPoMesicich(lekce.filter((l) => String(l.datum).substring(0, 10) < dnesIso), false), [lekce, dnesIso]);
  const pocet = (sk) => sk.reduce((a, r) => a + r.mesice.reduce((b, x) => b + x.lekce.length, 0), 0);
  // Nadcházející: otevřený první rok a první měsíc (nejbližší termíny).
  // Proběhlé: celá skupina zavřená, po otevření nejnovější rok a měsíc.
  const prvniN = nadchazejici[0] && { rok: nadchazejici[0].rok, m: nadchazejici[0].mesice[0].m };
  const prvniP = probehle[0] && { rok: probehle[0].rok, m: probehle[0].mesice[0].m };
  return (
    <div className="space-y-3">
      <SkupinaLekci nadpis="Nadcházející" pocet={pocet(nadchazejici)} ton="ok" vychoziOtevrena prazdno="Žádné nadcházející lekce.">
        {nadchazejici.map((r, i) => (
          <RokLekci key={r.rok} rok={r.rok} mesice={r.mesice} vypis={vypis} vychoziOtevreny={i === 0} otevritMesic={prvniN} />
        ))}
      </SkupinaLekci>
      <SkupinaLekci nadpis="Proběhlé" pocet={pocet(probehle)} prazdno="Zatím žádná proběhlá lekce.">
        {probehle.map((r, i) => (
          <RokLekci key={r.rok} rok={r.rok} mesice={r.mesice} vypis={vypis} vychoziOtevreny={i === 0} otevritMesic={prvniP} />
        ))}
      </SkupinaLekci>
    </div>
  );
}

// Obal detailu. V překryvu se chová jako detail klienta (klepnutí mimo zavírá,
// Esc taky), na celé stránce je to jen běžný obsah pod lištou se záložkami.
//
// Ovládací ikony mají vlastní lištu nad hlavičkou. Do GradientHeaderu nepatří:
// ten dává na mobilu každému potomkovi min-w-[7rem] a flex-1, takže čtvercová
// tlačítka řadu roztáhla a rozhodila (22.8.2026).
function Obal({ fullPage, onClose, onZalozka, children }) {
  useEffect(() => {
    if (fullPage) return undefined;
    const naKlavesu = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", naKlavesu);
    // Pozadí pod překryvem nemá rolovat, na mobilu to jinak ujíždí.
    const puvodni = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", naKlavesu);
      document.body.style.overflow = puvodni;
    };
  }, [fullPage, onClose]);

  if (fullPage) return <Stranka>{children}</Stranka>;

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm overflow-y-auto overscroll-contain" onClick={onClose}>
      <div className="min-h-full flex items-start justify-center p-0 sm:p-6">
        <div
          className="bg-slate-50 rounded-none sm:rounded-2xl shadow-2xl w-full max-w-5xl my-0 sm:my-4 min-h-[100dvh] sm:min-h-0 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-20 flex items-center justify-between gap-2 bg-slate-50/95 backdrop-blur border-b border-slate-200 px-3 sm:px-5 py-2.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bootcamp</span>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={onZalozka}
                title="Otevřít v záložce adminu (s Ctrl v novém okně prohlížeče)"
                aria-label="Otevřít v záložce adminu"
                className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-200/70 transition-colors">
                <Maximize2 className="w-[18px] h-[18px]" />
              </button>
              <button type="button" onClick={onClose} aria-label="Zavřít"
                className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-200/70 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-3 sm:p-5 space-y-4 sm:space-y-5">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Stejné tlačítko jako Vygenerovat nebo Přidat vedle něj, ať hlavička karty
// nemíchá dva tvary. Bez šipky: text sám říká, co se stane.
// Akce v radku ucastnice. Pilulka jasne rekne, ze je to tlacitko, a da prstu
// na mobilu dost velkou plochu.
const PILL_BASE =
  "inline-flex items-center justify-center min-h-[32px] px-3 py-1 rounded-full text-[13px] font-medium border transition-colors";
const PILL_NEUTRAL = `${PILL_BASE} border-slate-200 text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900`;
const PILL_DANGER = `${PILL_BASE} border-rose-200 text-rose-600 bg-rose-50/60 hover:bg-rose-100 hover:text-rose-700`;
const PILL_WARN = `${PILL_BASE} border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 hover:text-amber-800`;

// Zacatek lekce v case prohlizece. Trener sedi v Praze stejne jako server,
// tady jde jen o to, jestli termin uz byl, ne o presnou zonu.
function zacatekLekceKlient(l) {
  if (!l || !l.datum) return 0;
  const t = String(l.cas_od || "00:00");
  const d = new Date(String(l.datum).substring(0, 10) + "T" + (t.length === 5 ? t : "00:00") + ":00");
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

// Tlačítko do fialové lišty karty. Bílé na tmavém: světlé slate tlačítko tam
// nebylo vidět, text splýval s pozadím lišty (14.9.2026).
const AKCE_LISTY =
  "inline-flex shrink-0 items-center justify-center gap-1.5 min-h-[38px] px-3 py-1.5 rounded-xl border border-white/25 bg-white/10 text-white text-sm font-medium transition-colors active:scale-95 disabled:opacity-50 sm:hover:bg-white/20";

// 16.9.2026 (odsouhlasený mockup): z lišt karet zmizel text. Zůstalo kolečko
// a plus, jak je zvykem jinde v adminu. Vygenerovat, Místa, Načíst z faktur
// a Platnosti se přestěhovaly do bloků nastavení, kam patří, a stejně tak
// běží samy každou hodinu.
const AKCE_LISTY_IKONA =
  "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/10 text-white transition active:scale-95 disabled:opacity-50 sm:h-9 sm:w-9 sm:hover:bg-white/20";

// 14.9.2026: dřív to bylo plné tlačítko Rozbalit. Na telefonu bralo místo
// názvu a opakovalo se čtyřikrát pod sebou. Zbyla šipka, klepnout jde na celou
// lištu, stejně jako u sbalitelných karet ve zbytku adminu.
function Prepinac({ otevreno, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!!otevreno}
      aria-label={otevreno ? "Sbalit" : "Rozbalit"}
      title={otevreno ? "Sbalit" : "Rozbalit"}
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white transition active:scale-95 sm:h-9 sm:w-9 sm:hover:bg-white/15"
    >
      <ChevronDown className={`h-5 w-5 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
    </button>
  );
}

// Docházka jedné lekce. Tohle používá trenér po tréninku: vidí, kdo byl
// přihlášený, u každé odškrtne jestli dorazila, a když někdo přijde navíc,
// přidá ho z výběru zaplacených účastnic. Zápis jde i zpětně.
function DochazkaLekce({ muzeZapisovat, lekce, ucastnice, rezervace, busy, pridavana, setPridavana, onDochazka, onOdebrat, onPridat, onPoznamka }) {
  const budouciLekce = zacatekLekceKlient(lekce) > Date.now();
  // Poznamka k lekci. Trener si sem pise, co se cvicilo, at to pri dalsim
  // behu nemusi lovit z hlavy. Drzi se v mistnim stavu, uklada se tlacitkem.
  const [pozn, setPozn] = useState(lekce.poznamka || "");
  const [poznBusy, setPoznBusy] = useState(false);
  const uzka = useUzkaObrazovka();
  const [otevrenaPozn, setOtevrenaPozn] = useState(false);
  const [otevrenaAkce, setOtevrenaAkce] = useState(null);       // id rezervace s odkrytým odebráním
  const [pridavaniOtevreno, setPridavaniOtevreno] = useState(false);
  useEffect(() => { setPozn(lekce.poznamka || ""); }, [lekce.id, lekce.poznamka]);
  const poznZmenena = (pozn || "") !== (lekce.poznamka || "");
  const naLekci = rezervace.filter((r) => r.lekce_id === lekce.id && r.stav !== "zrusena");
  const podleId = new Map(ucastnice.map((u) => [u.id, u]));
  const radky = naLekci
    .map((r) => ({ r, u: podleId.get(r.ucastnice_id) }))
    .filter((x) => x.u)
    .sort((a, b) => a.u.jmeno.localeCompare(b.u.jmeno, "cs"));

  const uzJsou = new Set(naLekci.map((r) => r.ucastnice_id));
  const kPridani = ucastnice
    .filter((u) => u.stav === "zaplaceno" && !uzJsou.has(u.id))
    .sort((a, b) => a.jmeno.localeCompare(b.jmeno, "cs"));

  const tlacitko = (aktivni, barva) =>
    `inline-flex items-center gap-1.5 min-h-[38px] px-3 py-1.5 rounded-lg border text-sm font-medium transition ${
      aktivni ? barva : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100"}`;

  // 14.9.2026: na telefonu jiný tvar. Jméno bylo menší než v seznamu nad ním,
  // tlačítka Byla a Nedorazila brala celý řádek a jedenáct žen vyšlo přes
  // dvanáct set bodů. Poznámka je sbalená, protože se píše málokdy.
  const zapsano = (r) => r.stav === "dorazila" || r.stav === "nedorazila" || r.stav === "propadla";
  const nezapsanych = radky.filter(({ r }) => !zapsano(r)).length;

  async function vsechnyByly() {
    for (const { r } of radky) {
      if (!zapsano(r)) await onDochazka(r.id, "dorazila");   // eslint-disable-line no-await-in-loop
    }
  }

  const poznamkaBlok = muzeZapisovat ? (
    <>
      <textarea
        rows={3}
        value={pozn}
        onChange={(e) => setPozn(e.target.value)}
        placeholder="Např. kruhový trénink, 4 kola, důraz na střed těla."
        className="field w-full"
      />
      <div className="mt-2 flex items-center gap-2">
        <button type="button" disabled={busy || poznBusy || !poznZmenena}
          onClick={async () => { setPoznBusy(true); try { await onPoznamka(pozn); } finally { setPoznBusy(false); } }}
          className="btn btn-primary">
          <Save className="w-4 h-4" /> Uložit
        </button>
        {poznZmenena && !poznBusy && (
          <button type="button" onClick={() => setPozn(lekce.poznamka || "")} className={PILL_NEUTRAL}>Zahodit</button>
        )}
      </div>
    </>
  ) : (
    <div className="whitespace-pre-line text-[15px] text-slate-700">{lekce.poznamka}</div>
  );

  if (uzka) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-2">
        {(muzeZapisovat || lekce.poznamka) && (
          <div className="mb-2">
            <button type="button" onClick={() => setOtevrenaPozn((x) => !x)} aria-expanded={otevrenaPozn}
              className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-left">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-600">Co jste cvičili</span>
                {lekce.poznamka && !otevrenaPozn && (
                  <span className="block truncate text-[13px] text-slate-500">{lekce.poznamka}</span>
                )}
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${otevrenaPozn ? "" : "-rotate-90"}`} />
            </button>
            {otevrenaPozn && <div className="mt-2">{poznamkaBlok}</div>}
          </div>
        )}

        {radky.length === 0 ? (
          <div className="text-sm text-slate-500">Na tenhle termín zatím nikdo přihlášený není.</div>
        ) : (
          <>
            {muzeZapisovat && (
              <div className="mb-2 flex items-center gap-2 rounded-xl bg-brand-light px-3 py-2">
                <span className="text-[13px] font-bold text-brand-950 tabular-nums">
                  Zapsáno {radky.length - nezapsanych} z {radky.length}
                </span>
                <span className="flex-1" />
                {nezapsanych > 0 && (
                  <button type="button" disabled={busy} onClick={vsechnyByly}
                    className="text-[13px] font-bold text-brand-deep disabled:opacity-50">
                    Všechny byly
                  </button>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              {radky.map(({ r, u }) => {
                const byla = r.stav === "dorazila";
                const ne = r.stav === "nedorazila";
                const propadla = r.stav === "propadla";
                const podklad = byla ? "bg-emerald-50" : ne ? "bg-rose-50" : propadla ? "bg-amber-50" : "border border-slate-200";
                const barvaJmena = byla ? "text-emerald-900" : ne ? "text-rose-900" : propadla ? "text-amber-900" : "text-slate-900";
                const otevrene = otevrenaAkce === r.id;
                return (
                  <div key={r.id} className={`rounded-xl px-2.5 py-2 ${podklad}`}>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => muzeZapisovat && setOtevrenaAkce(otevrene ? null : r.id)}
                        className={`min-w-0 flex-1 text-left ${muzeZapisovat ? "" : "cursor-default"}`}>
                        <span className={`block truncate text-base font-bold leading-tight ${barvaJmena}`}>{u.jmeno}</span>
                        <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[12.5px] font-semibold tabular-nums text-slate-600 ${
                          byla || ne || propadla ? "bg-white/80" : "bg-slate-100"}`}>
                          {u.permice_celkem == null ? "bez omezení" : `zbývá ${u.zbyva} z ${u.permice_celkem}`}
                        </span>
                      </button>
                      {muzeZapisovat ? (
                        <>
                          <button type="button" disabled={busy} onClick={() => onDochazka(r.id, "dorazila")}
                            aria-label="Byla" title="Byla"
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition active:scale-95 ${
                              byla ? "bg-emerald-500 text-white" : "border border-slate-200 bg-white text-slate-400"}`}>
                            <Check className="h-5 w-5" />
                          </button>
                          <button type="button" disabled={busy} onClick={() => onDochazka(r.id, "nedorazila")}
                            aria-label="Nedorazila" title="Nedorazila"
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition active:scale-95 ${
                              ne ? "bg-rose-500 text-white" : "border border-slate-200 bg-white text-slate-400"}`}>
                            <X className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <span className="shrink-0 text-sm text-slate-500">
                          {byla ? "Byla" : ne ? "Nedorazila" : propadla ? "Vstup propadl" : "Přihlášena"}
                        </span>
                      )}
                    </div>
                    {otevrene && muzeZapisovat && (
                      <button type="button" disabled={busy} onClick={() => { setOtevrenaAkce(null); onOdebrat(r.id, u.jmeno); }}
                        className="mt-2 flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white text-[13px] font-semibold text-rose-600">
                        <Trash2 className="h-4 w-4" /> Odebrat z termínu a vrátit vstup
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {muzeZapisovat && (
          <div className="mt-2">
            {!pridavaniOtevreno ? (
              <button type="button" onClick={() => setPridavaniOtevreno(true)}
                className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-semibold text-brand-deep">
                <Plus className="h-4 w-4" /> {budouciLekce ? "Přihlásit někoho na termín" : "Přišel někdo navíc"}
              </button>
            ) : (
              <div className="space-y-2 rounded-xl border border-slate-200 p-2.5">
                <select className="field w-full" value={pridavana} onChange={(e) => setPridavana(e.target.value)}>
                  <option value="">Vyberte účastnici</option>
                  {kPridani.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.jmeno}{u.permice_celkem == null ? " (bez omezení)" : ` (zbývá ${u.zbyva})`}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button type="button" disabled={busy || !pridavana}
                    onClick={() => { onPridat(parseInt(pridavana, 10)); setPridavana(""); setPridavaniOtevreno(false); }}
                    className="btn btn-primary flex-1">
                    <Plus className="h-4 w-4" /> {budouciLekce ? "Přihlásit" : "Přidat a označit"}
                  </button>
                  <button type="button" onClick={() => { setPridavana(""); setPridavaniOtevreno(false); }} className={PILL_NEUTRAL}>Zavřít</button>
                </div>
                {kPridani.length === 0 && (
                  <div className="text-xs text-slate-500">Nabízí se jen účastnice se zaplaceným vstupem, které tu ještě nejsou.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-3 sm:mx-0 rounded-xl border border-slate-200 bg-white p-3">
      {muzeZapisovat && (
        <div className="mb-3 pb-3 border-b border-slate-100">
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Co jste cvičili</label>
          <textarea
            rows={2}
            value={pozn}
            onChange={(e) => setPozn(e.target.value)}
            placeholder="Např. kruhový trénink, 4 kola, důraz na střed těla."
            className="field w-full"
          />
          <div className="flex items-center gap-2 mt-2">
            <button type="button" disabled={busy || poznBusy || !poznZmenena}
              onClick={async () => { setPoznBusy(true); try { await onPoznamka(pozn); } finally { setPoznBusy(false); } }}
              className="btn btn-primary">
              <Save className="w-4 h-4" /> Uložit poznámku
            </button>
            {poznZmenena && !poznBusy && (
              <button type="button" onClick={() => setPozn(lekce.poznamka || "")} className={PILL_NEUTRAL}>
                Zahodit
              </button>
            )}
          </div>
        </div>
      )}
      {!muzeZapisovat && lekce.poznamka && (
        <div className="mb-3 pb-3 border-b border-slate-100">
          <div className="text-xs font-medium text-slate-500 mb-1">Co se cvičilo</div>
          <div className="text-[15px] text-slate-700 whitespace-pre-line">{lekce.poznamka}</div>
        </div>
      )}
      {radky.length === 0 ? (
        <div className="text-sm text-slate-500">Na tenhle termín zatím nikdo přihlášený není.</div>
      ) : (
        <div className="space-y-2">
          {radky.map(({ r, u }) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 last:border-0 pb-2 last:pb-0">
              <div className="min-w-0">
                <div className="font-medium text-slate-900 text-sm">{u.jmeno}</div>
                <div className="text-xs text-slate-500 tabular-nums">
                  {u.tarif ? `${u.tarif}, ` : ""}
                  {u.permice_celkem == null ? "bez omezení" : `zbývá ${u.zbyva} z ${u.permice_celkem}`}
                </div>
              </div>
              {!muzeZapisovat ? (
                <span className="text-sm text-slate-500">
                  {r.stav === "dorazila" ? "Byla" : r.stav === "nedorazila" ? "Nedorazila" : r.stav === "propadla" ? "Vstup propadl" : "Přihlášena"}
                </span>
              ) : (
              <div className="flex items-center gap-1.5 flex-wrap">
                <button type="button" disabled={busy} onClick={() => onDochazka(r.id, "dorazila")}
                  className={tlacitko(r.stav === "dorazila", "border-emerald-300 bg-emerald-50 text-emerald-700")}>
                  <CheckCircle2 className="w-4 h-4" /> Byla
                </button>
                <button type="button" disabled={busy} onClick={() => onDochazka(r.id, "nedorazila")}
                  className={tlacitko(r.stav === "nedorazila", "border-rose-300 bg-rose-50 text-rose-700")}>
                  <XCircle className="w-4 h-4" /> Nedorazila
                </button>
                <button type="button" disabled={busy} onClick={() => onOdebrat(r.id, u.jmeno)}
                  title="Odebrat z termínu a vrátit vstup"
                  className="inline-flex items-center justify-center w-[38px] h-[38px] rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-rose-600 hover:border-rose-200">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              )}
            </div>
          ))}
        </div>
      )}

      {muzeZapisovat && (
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="text-xs font-medium text-slate-500 mb-1.5">{budouciLekce ? "Přihlásit někoho na termín" : "Přišel někdo navíc?"}</div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="field flex-1 min-w-[180px]"
            value={pridavana}
            onChange={(e) => setPridavana(e.target.value)}
          >
            <option value="">Vyberte účastnici</option>
            {kPridani.map((u) => (
              <option key={u.id} value={u.id}>
                {u.jmeno}{u.permice_celkem == null ? " (bez omezení)" : ` (zbývá ${u.zbyva})`}
              </option>
            ))}
          </select>
          <button type="button" disabled={busy || !pridavana}
            onClick={() => { onPridat(parseInt(pridavana, 10)); setPridavana(""); }}
            className="btn btn-primary">
            <Plus className="w-4 h-4" /> {budouciLekce ? "Přihlásit" : "Přidat a označit"}
          </button>
        </div>
        {kPridani.length === 0 && (
          <div className="text-xs text-slate-500 mt-1.5">
            Nabízí se jen účastnice se zaplaceným vstupem, které tu ještě nejsou.
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// Karta dnešní, nebo když dnes žádná není, nejbližší lekce. Kreslí se jen na
// telefonu: trenér otevírá bootcamp skoro vždycky kvůli docházce a tahle karta
// mu ji dá na jedno klepnutí (14.9.2026).
function KartaDnesniLekce({ lekce, jeDnes, stav, muzeZapisovat, otevreno, onToggle, children }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-2xl border-2 border-brand bg-white shadow-glass">
      <div className="flex items-center gap-2 bg-brand-light px-4 py-2">
        <span className="h-2 w-2 shrink-0 rounded-full bg-brand" />
        <span className="text-xs font-bold uppercase tracking-wide text-brand-950">
          {jeDnes ? "Dnešní lekce" : "Nejbližší lekce"}
        </span>
      </div>
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-xl font-bold text-slate-900">{denVTydnu(lekce.datum)} {den(lekce.datum)}</span>
          <span className="text-base tabular-nums text-slate-500">{lekce.cas_od}{lekce.cas_do ? ` až ${lekce.cas_do}` : ""}</span>
        </div>
        {lekce.trener_name && <div className="mt-0.5 text-sm text-slate-500">{lekce.trener_name}</div>}
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
            přihlášeno {stav.prihlasenych} z {lekce.kapacita}
          </span>
          {muzeZapisovat && jeDnes && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              stav.hotovo ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {stav.hotovo ? "docházka zapsaná" : "docházka nezapsaná"}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!!otevreno}
          className="mt-3 flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 font-semibold text-white transition active:scale-[0.99]"
        >
          <CheckCircle2 className="h-4 w-4" />
          {otevreno ? "Skrýt" : (jeDnes && muzeZapisovat ? "Zapsat docházku" : "Docházka")}
        </button>
        {otevreno && <div className="mt-3">{children}</div>}
      </div>
    </div>
  );
}

// Telefon a e-mail v detailu účastnice. Klepnutím na hodnotu se volá nebo píše,
// ikona vpravo hodnotu zkopíruje do schránky (14.9.2026).
function RadekKontaktu({ ikona: Ikona, hodnota, href, cislice = false }) {
  const [zkopirovano, setZkopirovano] = useState(false);
  async function kopiruj() {
    try {
      await navigator.clipboard.writeText(String(hodnota));
      setZkopirovano(true);
      setTimeout(() => setZkopirovano(false), 1500);
    } catch (_) { /* schránka není k dispozici, ticho */ }
  }
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200">
      <a href={href} className="flex min-w-0 flex-1 items-center gap-2.5 py-2.5 pl-3">
        <Ikona className="h-4 w-4 shrink-0 text-brand-deep" />
        <span className={`min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-800 ${cislice ? "tabular-nums" : ""}`}>
          {hodnota}
        </span>
      </a>
      <button type="button" onClick={kopiruj} aria-label="Kopírovat" title="Kopírovat"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-400 transition active:scale-95">
        {zkopirovano ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function IkonaStavu({ stav }) {
  if (stav === "dorazila") return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  if (stav === "nedorazila") return <XCircle className="w-4 h-4 text-rose-500" />;
  if (stav === "propadla") return <Ban className="w-4 h-4 text-amber-600" />;
  if (stav === "rezervovano") return <Circle className="w-4 h-4 text-brand-600 fill-brand-100" />;
  return <Circle className="w-4 h-4 text-slate-200" />;
}

// Náhradní lekce (7.9.2026). Datum je omezené na dnešek a dál — lekce v minulosti
// by nešla obsadit a rozbila by docházku. Server to hlídá znovu.
function FormularLekce({ hodnoty, setHodnoty, treneri, onUloz, onZrus, busy, behId, misto }) {
  const dnes = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague" }).format(new Date());
  const set = (k, v) => setHodnoty({ ...hodnoty, [k]: v });
  // Náhled SMS: kolika ženám dojde a co v ní bude. Načítá se, až když je
  // vyplněné datum a čas — dřív by ukázka byla useless.
  const [nahled, setNahled] = useState(null);
  useEffect(() => {
    let zrus = false;
    if (!behId || !hodnoty.datum || !hodnoty.cas_od) { setNahled(null); return undefined; }
    (async () => {
      try {
        const r = await apiFetch("/api/bootcampy", {
          method: "POST",
          body: JSON.stringify({
            akce: "sms-nahled-lekce", bootcamp_id: behId,
            datum: hodnoty.datum, cas_od: hodnoty.cas_od, misto: hodnoty.misto || misto || "",
          }),
        });
        const j = await r.json();
        if (!zrus && r.ok) setNahled(j);
      } catch { /* náhled není kritický */ }
    })();
    return () => { zrus = true; };
  }, [behId, hodnoty.datum, hodnoty.cas_od, hodnoty.misto, misto]);
  const chybi = !hodnoty.datum || !hodnoty.cas_od;
  const vMinulosti = !!hodnoty.datum && hodnoty.datum < dnes;
  return (
    <div className="mb-4 rounded-xl border border-brand-200 bg-brand-50/60 p-3">
      <div className="text-sm font-bold text-slate-800 mb-2">Náhradní lekce</div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <Pole label="Datum">
          <input type="date" className="field" min={dnes} value={hodnoty.datum} onChange={(e) => set("datum", e.target.value)} />
        </Pole>
        <Pole label="Čas od">
          <input className="field" placeholder="18:00" value={hodnoty.cas_od} onChange={(e) => set("cas_od", e.target.value)} />
        </Pole>
        <Pole label="Čas do">
          <input className="field" placeholder="19:00" value={hodnoty.cas_do} onChange={(e) => set("cas_do", e.target.value)} />
        </Pole>
        <Pole label="Trenér">
          <select className="field" value={hodnoty.trener_id} onChange={(e) => set("trener_id", e.target.value)}>
            <option value="">Jako u běhu</option>
            {(treneri || []).map((t) => <option key={t.id} value={t.id}>{t.jmeno}</option>)}
          </select>
        </Pole>
      </div>
      <div className="mt-2">
        <Pole label="Poznámka (volitelně)" hint="Třeba za co je to náhrada, účastnice ji uvidí u lekce.">
          <input className="field" value={hodnoty.poznamka} onChange={(e) => set("poznamka", e.target.value)} placeholder="Náhrada za zrušenou lekci 10. 9." />
        </Pole>
      </div>
      {vMinulosti && (
        <div className="mt-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          Lekci nejde založit do minulosti, nikdo by se na ni nepřihlásil.
        </div>
      )}
      <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
        <input type="checkbox" className="mt-0.5 w-4 h-4 accent-brand-600" checked={hodnoty.sms !== false}
          onChange={(e) => set("sms", e.target.checked)} />
        <span>
          Poslat SMS účastnicím běhu
          {nahled ? <b> ({nahled.prijemcu} {nahled.prijemcu === 1 ? "žena" : nahled.prijemcu >= 2 && nahled.prijemcu <= 4 ? "ženy" : "žen"}{nahled.bezTelefonu ? `, ${nahled.bezTelefonu} bez telefonu` : ""})</b> : null}
          <span className="block text-xs text-slate-500">Jen ženy z tohohle běhu, které mají zaplaceno a ještě jim zbývá vstup, jiné se na termín ani přihlásit nemůžou.</span>
        </span>
      </label>
      {hodnoty.sms !== false && nahled?.ukazka && (
        <div className="mt-2 text-xs text-slate-600 bg-white border border-slate-200 rounded-xl px-3 py-2">
          <div className="font-semibold text-slate-500 mb-0.5">Text SMS</div>
          {nahled.ukazka}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onUloz} disabled={busy || chybi || vMinulosti}
          className="btn btn-primary">
          <Plus className="w-4 h-4" /> {busy ? "Ukládám…" : "Přidat lekci"}
        </button>
        <button type="button" onClick={onZrus} className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 min-h-[44px] sm:min-h-0">
          Zrušit
        </button>
      </div>
    </div>
  );
}

function FormularUcastnice({ hodnoty, setHodnoty, tarify, onUloz, onZrus, busy }) {
  const set = (k, v) => setHodnoty({ ...hodnoty, [k]: v });
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Pole label="Jméno"><input className="field" value={hodnoty.jmeno || ""} onChange={(e) => set("jmeno", e.target.value)} /></Pole>
        <Pole label="E-mail"><input className="field" value={hodnoty.email || ""} onChange={(e) => set("email", e.target.value)} /></Pole>
        <Pole label="Telefon" hint="Devět číslic. Bez něj se účastnice nepřihlásí do aplikace.">
          <input className="field" value={hodnoty.telefon || ""} onChange={(e) => set("telefon", e.target.value)} />
        </Pole>
        <Pole label="Tarif">
          <select className="field" value={hodnoty.tarif || ""} onChange={(e) => {
            const t = (tarify || []).find((x) => x.nazev === e.target.value);
            setHodnoty({ ...hodnoty, tarif: e.target.value, permice_celkem: t ? (t.vstupy == null ? "" : t.vstupy) : hodnoty.permice_celkem });
          }}>
            <option value="">Nevybráno</option>
            {(tarify || []).map((t) => <option key={t.nazev} value={t.nazev}>{t.nazev}</option>)}
          </select>
        </Pole>
        <Pole label="Vstupů" hint="Prázdné znamená bez omezení.">
          <input className="field" value={hodnoty.permice_celkem === null ? "" : (hodnoty.permice_celkem ?? "")} onChange={(e) => set("permice_celkem", e.target.value)} />
        </Pole>
        <Pole label="Stav">
          <select className="field" value={hodnoty.stav || "prihlasena"} onChange={(e) => set("stav", e.target.value)}>
            {STAVY_UCASTI.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Pole>
        <Pole label="Vstupy vyčerpat do" hint="Po tomhle dni se na termín nepřihlásí. Prázdné znamená bez omezení.">
          <input type="date" className="field" value={hodnoty.platnost_do || ""} onChange={(e) => set("platnost_do", e.target.value)} />
        </Pole>
      </div>
      <div className="mt-3">
        <Pole label="Poznámka trenéra">
          <input className="field" value={hodnoty.poznamka_trener || ""} onChange={(e) => set("poznamka_trener", e.target.value)} />
        </Pole>
      </div>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onUloz} disabled={busy}
          className="btn btn-primary">
          <Save className="w-4 h-4" /> Uložit
        </button>
        <button type="button" onClick={onZrus}
          className="btn btn-secondary">
          <X className="w-4 h-4" /> Zrušit
        </button>
      </div>
    </div>
  );
}

// Malé okno s volbami pro jedno políčko mřížky. Nativní dialogy nepoužíváme.
function ModalBunky({ bunka, busy, onZavri, onAkce }) {
  const { lekce, ucastnice, rezervace } = bunka;
  const volby = [];
  if (!rezervace) {
    volby.push({ label: "Rezervovat termín", tone: "primary", telo: { akce: "rezervovat", lekce_id: lekce.id, ucastnice_id: ucastnice.id }, hotovo: "Rezervováno." });
  } else {
    if (rezervace.stav !== "dorazila") volby.push({ label: "Označit, že dorazila", tone: "primary", telo: { akce: "dochazka", id: rezervace.id, stav: "dorazila" }, hotovo: "Označeno." });
    if (rezervace.stav !== "nedorazila") volby.push({ label: "Označit, že nedorazila", tone: "warning", telo: { akce: "dochazka", id: rezervace.id, stav: "nedorazila" }, hotovo: "Označeno." });
    if (rezervace.stav !== "rezervovano") volby.push({ label: "Zpět na rezervováno", tone: "plain", telo: { akce: "dochazka", id: rezervace.id, stav: "rezervovano" }, hotovo: "Vráceno." });
    volby.push({ label: "Zrušit rezervaci, vstup se vrátí", tone: "plain", telo: { akce: "zrusit-rezervaci", id: rezervace.id, propadne: false }, hotovo: "Rezervace zrušena." });
    volby.push({ label: "Zrušit rezervaci, vstup propadne", tone: "danger", telo: { akce: "zrusit-rezervaci", id: rezervace.id, propadne: true }, hotovo: "Vstup propadl." });
  }
  const tridy = {
    primary: "bg-brand-600 hover:bg-brand-700 text-white",
    warning: "bg-amber-600 hover:bg-amber-700 text-white",
    danger: "border border-rose-200 text-rose-700 hover:bg-rose-50",
    plain: "border border-slate-200 text-slate-700 hover:bg-slate-50",
  };
  return (
    /* klavesnice-ok: karta účastnice jen s odkazy a tlačítky, žádné pole (14. 9. 2026, fáze 6) */
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-brand-dark/50 p-0 sm:p-4 backdrop-blur-sm" onClick={onZavri}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-sm p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-base font-bold text-slate-900">{ucastnice.jmeno}</h3>
          <button type="button" onClick={onZavri} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-slate-500 mb-4">
          {denVTydnu(lekce.datum)} {den(lekce.datum)}, {lekce.cas_od}
        </div>
        <div className="space-y-2">
          {volby.map((v, i) => (
            <button key={i} type="button" disabled={busy} onClick={() => onAkce(v.telo, v.hotovo)}
              className={`w-full text-left min-h-[44px] px-3 py-2.5 rounded-xl text-sm font-medium disabled:opacity-60 ${tridy[v.tone]}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
